import { Option, Schema, SchemaIssue, Struct } from "effect";
import { ClaudeModelOptions, CodexModelOptions } from "./model";
import { PlaneIssueRef, projectPlaneFields } from "./plane";
import { PullRequestRef } from "./pullRequest";
import { ProviderMentionReference, ProviderSkillReference } from "./providerDiscovery";
import {
  DEFAULT_PROJECT_ENVIRONMENT_KIND,
  EnvironmentCloneOrigin,
  EnvironmentTrialSnapshot,
  ProjectEnvironment,
  ProjectEnvironmentKind,
  ProjectEnvironmentModificationKind,
  ProjectEnvironmentName,
  ProjectEnvironmentSyncState,
} from "./environments";
import { LocalActionId, ProjectLocalAction } from "./localExec";
import { PrPreview } from "./prPreviews";
import {
  ApprovalRequestId,
  CheckpointRef,
  CommandId,
  EnvironmentId,
  EventId,
  IsoDateTime,
  ManagedAgentId,
  MessageId,
  NonNegativeInt,
  PositiveInt,
  PrPreviewId,
  ProjectId,
  ProviderItemId,
  ThreadId,
  TrimmedNonEmptyString,
  TurnId,
  UserId,
} from "./baseSchemas";
import { TurnModelUsage, TurnUsageKind } from "./tokenUsage";
import { TagColor } from "./tags";

export const PROJECT_DESCRIPTION_MAX_LENGTH = 500;
export const ProjectDescription = TrimmedNonEmptyString.check(
  Schema.isMaxLength(PROJECT_DESCRIPTION_MAX_LENGTH),
);

export const ORCHESTRATION_WS_METHODS = {
  getSnapshot: "orchestration.getSnapshot",
  dispatchCommand: "orchestration.dispatchCommand",
  repairState: "orchestration.repairState",
  getTurnDiff: "orchestration.getTurnDiff",
  getFullThreadDiff: "orchestration.getFullThreadDiff",
  replayEvents: "orchestration.replayEvents",
  subscribeShell: "orchestration.subscribeShell",
  unsubscribeShell: "orchestration.unsubscribeShell",
  subscribeThread: "orchestration.subscribeThread",
  unsubscribeThread: "orchestration.unsubscribeThread",
  loadOlderThreadHistory: "orchestration.loadOlderThreadHistory",
  replayThreadEvents: "orchestration.replayThreadEvents",
  setProjectRepoCredentials: "orchestration.setProjectRepoCredentials",
  validateProjectRepoCredentials: "orchestration.validateProjectRepoCredentials",
  clearProjectRepoCredentials: "orchestration.clearProjectRepoCredentials",
  createProjectFromRepository: "orchestration.createProjectFromRepository",
  startThreadDevServer: "orchestration.startThreadDevServer",
  stopThreadDevServer: "orchestration.stopThreadDevServer",
  updateThreadDevCommand: "orchestration.updateThreadDevCommand",
  getThreadDevServer: "orchestration.getThreadDevServer",
  setThreadDevServerVisibility: "orchestration.setThreadDevServerVisibility",
  getThreadUserState: "orchestration.getThreadUserState",
  markThreadVisited: "orchestration.markThreadVisited",
  // The thread directory: one page of the whole installation, filtered in SQL.
  // Not a subscription and not a snapshot — see `threadDirectory.ts`.
  listThreadDirectory: "orchestration.listThreadDirectory",
} as const;

export const ORCHESTRATION_WS_CHANNELS = {
  domainEvent: "orchestration.domainEvent",
  shellEvent: "orchestration.shellEvent",
  threadEvent: "orchestration.threadEvent",
  devServerStatusChanged: "orchestration.devServerStatusChanged",
} as const;

export const PROVIDER_KINDS = ["codex", "claudeAgent"] as const;
export const ProviderKind = Schema.Literals(PROVIDER_KINDS);
export type ProviderKind = typeof ProviderKind.Type;

/**
 * Narrow a value read back from storage or an untyped payload. Derived from
 * `PROVIDER_KINDS` so adding a provider cannot leave a hand-written check
 * behind.
 */
export const isProviderKind = (value: unknown): value is ProviderKind =>
  typeof value === "string" && (PROVIDER_KINDS as readonly string[]).includes(value);
export const ProviderApprovalPolicy = Schema.Literals([
  "untrusted",
  "on-failure",
  "on-request",
  "never",
]);
export type ProviderApprovalPolicy = typeof ProviderApprovalPolicy.Type;
export const ProviderSandboxMode = Schema.Literals([
  "read-only",
  "workspace-write",
  "danger-full-access",
]);
export type ProviderSandboxMode = typeof ProviderSandboxMode.Type;
export const DEFAULT_PROVIDER_KIND: ProviderKind = "codex";

/**
 * `agentId` names the managed agent (Settings → Agents) whose instructions and
 * tool allowlist apply to the session. Optional and additive: every selection
 * persisted in `model_selection_json` before managed agents existed decodes
 * unchanged, and an absent id means "plain provider session, no agent applied".
 * It rides inside the selection rather than beside it because it is part of
 * *what to run* — changing it has to restart the provider session the same way
 * changing the model does.
 */
export const CodexModelSelection = Schema.Struct({
  provider: Schema.Literal("codex"),
  model: TrimmedNonEmptyString,
  agentId: Schema.optional(ManagedAgentId),
  options: Schema.optional(CodexModelOptions),
});
export type CodexModelSelection = typeof CodexModelSelection.Type;

export const ClaudeModelSelection = Schema.Struct({
  provider: Schema.Literal("claudeAgent"),
  model: TrimmedNonEmptyString,
  agentId: Schema.optional(ManagedAgentId),
  options: Schema.optional(ClaudeModelOptions),
});
export type ClaudeModelSelection = typeof ClaudeModelSelection.Type;

export const ModelSelection = Schema.Union([CodexModelSelection, ClaudeModelSelection]);
export type ModelSelection = typeof ModelSelection.Type;

export const CodexProviderStartOptions = Schema.Struct({
  binaryPath: Schema.optional(TrimmedNonEmptyString),
  homePath: Schema.optional(TrimmedNonEmptyString),
});

export const ClaudeProviderStartOptions = Schema.Struct({
  binaryPath: Schema.optional(TrimmedNonEmptyString),
  permissionMode: Schema.optional(TrimmedNonEmptyString),
  maxThinkingTokens: Schema.optional(NonNegativeInt),
});

export const ProviderStartOptions = Schema.Struct({
  codex: Schema.optional(CodexProviderStartOptions),
  claudeAgent: Schema.optional(ClaudeProviderStartOptions),
});
export type ProviderStartOptions = typeof ProviderStartOptions.Type;

export const RuntimeMode = Schema.Literals(["approval-required", "full-access"]);
export type RuntimeMode = typeof RuntimeMode.Type;
export const DEFAULT_RUNTIME_MODE: RuntimeMode = "full-access";
export const ProviderInteractionMode = Schema.Literals(["default", "plan"]);
export type ProviderInteractionMode = typeof ProviderInteractionMode.Type;
export const DEFAULT_PROVIDER_INTERACTION_MODE: ProviderInteractionMode = "default";
export const ProviderRequestKind = Schema.Literals(["command", "file-read", "file-change"]);
export type ProviderRequestKind = typeof ProviderRequestKind.Type;
export const AssistantDeliveryMode = Schema.Literals(["buffered", "streaming"]);
export type AssistantDeliveryMode = typeof AssistantDeliveryMode.Type;
// Queue is the default "send message" behavior; steer is an urgent redirect.
export const TurnDispatchMode = Schema.Literals(["queue", "steer"]);
export type TurnDispatchMode = typeof TurnDispatchMode.Type;
export const DEFAULT_TURN_DISPATCH_MODE: TurnDispatchMode = "queue";
export const ProviderReviewTarget = Schema.Union([
  Schema.Struct({
    type: Schema.Literal("uncommittedChanges"),
  }),
  Schema.Struct({
    type: Schema.Literal("baseBranch"),
    branch: TrimmedNonEmptyString,
  }),
]);
export type ProviderReviewTarget = typeof ProviderReviewTarget.Type;
export const ProviderApprovalDecision = Schema.Literals([
  "accept",
  "acceptForSession",
  "decline",
  "cancel",
]);
export type ProviderApprovalDecision = typeof ProviderApprovalDecision.Type;
export const ProviderUserInputAnswers = Schema.Record(Schema.String, Schema.Unknown);
export type ProviderUserInputAnswers = typeof ProviderUserInputAnswers.Type;
export const ThreadHandoffBootstrapStatus = Schema.Literals(["pending", "completed"]);
export type ThreadHandoffBootstrapStatus = typeof ThreadHandoffBootstrapStatus.Type;
export const ThreadEnvironmentMode = Schema.Literals(["local", "worktree"]);
export type ThreadEnvironmentMode = typeof ThreadEnvironmentMode.Type;

export const OrchestrationMessageSource = Schema.Literals([
  "native",
  "handoff-import",
  "fork-import",
]);
export type OrchestrationMessageSource = typeof OrchestrationMessageSource.Type;

export const PROVIDER_SEND_TURN_MAX_INPUT_CHARS = 120_000;
export const PROVIDER_SEND_TURN_MAX_ATTACHMENTS = 8;
export const PROVIDER_SEND_TURN_MAX_IMAGE_BYTES = 10 * 1024 * 1024;
/**
 * Non-image attachments never enter the model's context window as bytes — the
 * server persists them and hands the agent a path — so the ceiling is set by
 * the transport rather than by a provider: the data URL crosses the websocket
 * in a single frame, and 25 MB of file is ~34 MB of base64.
 */
export const PROVIDER_SEND_TURN_MAX_FILE_BYTES = 25 * 1024 * 1024;
const PROVIDER_SEND_TURN_MAX_IMAGE_DATA_URL_CHARS = 14_000_000;
const PROVIDER_SEND_TURN_MAX_FILE_DATA_URL_CHARS = 34_000_000;

/**
 * Aggregate ceiling for everything one message attaches.
 *
 * The per-attachment limits are about what a single file may weigh; this one is
 * about what a single websocket frame may weigh, and they are not the same
 * question. Eight attachments at their individual maximum are ~267 MB of base64
 * in one frame, well past what the server will read — and past it in the worst
 * possible way, because `ws` answers an oversized frame by closing the
 * connection (1009) rather than by rejecting the message, so the person sees
 * "Reconnecting…" and loses what they wrote with no clue why.
 *
 * 60 MB of attachments is ~80 MB of base64, which leaves the frame roughly 20 MB
 * of headroom under `WEBSOCKET_MAX_PAYLOAD_BYTES` for the prompt and envelope.
 * Both numbers move together or neither moves.
 */
export const PROVIDER_SEND_TURN_MAX_ATTACHMENTS_TOTAL_BYTES = 60 * 1024 * 1024;

/**
 * Largest websocket frame the server will accept.
 *
 * Written down rather than inherited: `ws` defaults to 100 MiB, so the ceiling
 * that decides whether a legitimate message survives used to live in a
 * dependency's default instead of next to the limit it has to clear. Sized from
 * `PROVIDER_SEND_TURN_MAX_ATTACHMENTS_TOTAL_BYTES` above.
 */
export const WEBSOCKET_MAX_PAYLOAD_BYTES = 100 * 1024 * 1024;
const CHAT_ATTACHMENT_ID_MAX_CHARS = 128;
// Correlation id is command id by design in this model.
export const CorrelationId = CommandId;
export type CorrelationId = typeof CorrelationId.Type;

const ChatAttachmentId = TrimmedNonEmptyString.check(
  Schema.isMaxLength(CHAT_ATTACHMENT_ID_MAX_CHARS),
  Schema.isPattern(/^[a-z0-9_-]+$/i),
);
export type ChatAttachmentId = typeof ChatAttachmentId.Type;

export const ChatImageAttachment = Schema.Struct({
  type: Schema.Literal("image"),
  id: ChatAttachmentId,
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100), Schema.isPattern(/^image\//i)),
  sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES)),
});
export type ChatImageAttachment = typeof ChatImageAttachment.Type;

const UploadChatImageAttachment = Schema.Struct({
  type: Schema.Literal("image"),
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100), Schema.isPattern(/^image\//i)),
  sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_IMAGE_BYTES)),
  dataUrl: TrimmedNonEmptyString.check(
    Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_IMAGE_DATA_URL_CHARS),
  ),
});
export type UploadChatImageAttachment = typeof UploadChatImageAttachment.Type;

/**
 * Anything attached that is not an image.
 *
 * A separate member rather than a looser `ChatImageAttachment` because the two
 * reach the model through different channels and carry different ceilings: an
 * image becomes a base64 content block inside the turn, a file becomes a path
 * the agent opens with its own tools. Collapsing them would leave every
 * consumer re-deriving which one it is holding from the mime type.
 *
 * `mimeType` carries no pattern here on purpose. The set of acceptable types is
 * an allowlist that also has to agree with the file name and with what the HTTP
 * route may serve inline, which is more than a schema check can express;
 * `resolveAttachmentType` in `@taskin-code/shared/attachmentKinds` is the one
 * place that decides it, and both the composer and the websocket route ask it.
 */
export const ChatFileAttachment = Schema.Struct({
  type: Schema.Literal("file"),
  id: ChatAttachmentId,
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100)),
  sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_FILE_BYTES)),
});
export type ChatFileAttachment = typeof ChatFileAttachment.Type;

const UploadChatFileAttachment = Schema.Struct({
  type: Schema.Literal("file"),
  name: TrimmedNonEmptyString.check(Schema.isMaxLength(255)),
  mimeType: TrimmedNonEmptyString.check(Schema.isMaxLength(100)),
  sizeBytes: NonNegativeInt.check(Schema.isLessThanOrEqualTo(PROVIDER_SEND_TURN_MAX_FILE_BYTES)),
  dataUrl: TrimmedNonEmptyString.check(
    Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_FILE_DATA_URL_CHARS),
  ),
});
export type UploadChatFileAttachment = typeof UploadChatFileAttachment.Type;

export const ChatAttachment = Schema.Union([ChatImageAttachment, ChatFileAttachment]);
export type ChatAttachment = typeof ChatAttachment.Type;
export const UploadChatAttachment = Schema.Union([
  UploadChatImageAttachment,
  UploadChatFileAttachment,
]);
export type UploadChatAttachment = typeof UploadChatAttachment.Type;

/**
 * The list of attachments a client may hand over in one message.
 *
 * One schema rather than the check repeated per command, because the aggregate
 * limit is the one an attacker and an enthusiastic user both reach the same
 * way: several files that are each perfectly legal. Every route that accepts
 * uploads decodes through this, so no route can be the one that forgot.
 */
export const UploadChatAttachments = Schema.Array(UploadChatAttachment).check(
  Schema.isMaxLength(PROVIDER_SEND_TURN_MAX_ATTACHMENTS),
  Schema.makeFilter(
    (attachments) => {
      const totalBytes = attachments.reduce((total, attachment) => total + attachment.sizeBytes, 0);
      return (
        totalBytes <= PROVIDER_SEND_TURN_MAX_ATTACHMENTS_TOTAL_BYTES ||
        new SchemaIssue.InvalidValue(Option.some(totalBytes), {
          message: `attachments must total at most ${PROVIDER_SEND_TURN_MAX_ATTACHMENTS_TOTAL_BYTES} bytes`,
        })
      );
    },
    { identifier: "UploadChatAttachmentsTotalBytes" },
  ),
);

export const ProjectScriptIcon = Schema.Literals([
  "play",
  "test",
  "lint",
  "configure",
  "build",
  "debug",
  "chat",
  "compose",
]);
export type ProjectScriptIcon = typeof ProjectScriptIcon.Type;

/**
 * What a project action does when it runs.
 *
 * - `terminal`: `command` is a shell command run against the thread workspace.
 * - `chat`: `command` holds a canned prompt that is submitted to the active
 *   thread as a user turn, so the agent executes it.
 * - `compose`: `command` holds a canned prompt that is dropped into the chat
 *   composer without being sent, so the user can edit it before submitting.
 */
export const ProjectScriptKind = Schema.Literals(["terminal", "chat", "compose"]);
export type ProjectScriptKind = typeof ProjectScriptKind.Type;

/**
 * Where a `terminal` action executes. Ignored by `chat` and `compose` actions.
 *
 * - `terminal`: open (or reuse) a terminal tab and type the command into it.
 * - `background`: the server runs the command in the worktree and reports the
 *   result, for users who have no terminal access.
 */
export const ProjectScriptRunMode = Schema.Literals(["terminal", "background"]);
export type ProjectScriptRunMode = typeof ProjectScriptRunMode.Type;

export const DEFAULT_PROJECT_SCRIPT_KIND: ProjectScriptKind = "terminal";
export const DEFAULT_PROJECT_SCRIPT_RUN_MODE: ProjectScriptRunMode = "terminal";

/**
 * Upper bound for `ProjectScript.command`. Shell commands are far shorter than
 * this; the ceiling is sized for `chat` actions, whose "command" is a prompt.
 */
export const PROJECT_SCRIPT_COMMAND_MAX_CHARS = 8000;

/**
 * `kind`, `runMode` and `runOnThreadArchive` decode with defaults so the
 * `scripts_json` rows written before each of those fields existed keep
 * decoding — they are absent there, and every such action is a terminal
 * command run in a terminal that does nothing on archive.
 */
export const ProjectScript = Schema.Struct({
  id: TrimmedNonEmptyString,
  name: TrimmedNonEmptyString,
  kind: ProjectScriptKind.pipe(Schema.withDecodingDefault(() => DEFAULT_PROJECT_SCRIPT_KIND)),
  command: TrimmedNonEmptyString.check(Schema.isMaxLength(PROJECT_SCRIPT_COMMAND_MAX_CHARS)),
  runMode: ProjectScriptRunMode.pipe(
    Schema.withDecodingDefault(() => DEFAULT_PROJECT_SCRIPT_RUN_MODE),
  ),
  icon: ProjectScriptIcon,
  runOnWorktreeCreate: Schema.Boolean,
  /**
   * Run this action when a thread of the project is archived, in the thread's
   * worktree. Always executed server-side regardless of `runMode`: archiving
   * happens from the sidebar, with no thread surface open to host a terminal.
   */
  runOnThreadArchive: Schema.Boolean.pipe(Schema.withDecodingDefault(() => false)),
});
export type ProjectScript = typeof ProjectScript.Type;

export const ProjectRepoCredentialStatus = Schema.Literals(["none", "valid", "invalid"]);
export type ProjectRepoCredentialStatus = typeof ProjectRepoCredentialStatus.Type;

const nullableText = Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
  Schema.withDecodingDefault(() => null),
);

/**
 * Repository + credential state carried on every projected project.
 *
 * Shared verbatim by the full and shell project schemas so the settings UI can
 * rehydrate what the server already knows — the URL, whether the stored PAT
 * validated, and why it failed if it didn't — instead of showing an empty form
 * on every reload. `repoCredentialError` mirrors `token_last_error` and
 * `repoCredentialValidatedAt` mirrors `token_validated_at`, which is what makes
 * an expired token diagnosable without re-entering it.
 */
const projectRepositoryFields = {
  repositoryUrl: nullableText,
  repositoryDefaultBranch: nullableText,
  devCommand: nullableText,
  repoCredentialStatus: Schema.optional(ProjectRepoCredentialStatus).pipe(
    Schema.withDecodingDefault(() => "none" as ProjectRepoCredentialStatus),
  ),
  repoCredentialError: nullableText,
  repoCredentialValidatedAt: Schema.optional(Schema.NullOr(IsoDateTime)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
} as const;

const projectBaseFields = {
  id: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  defaultModelSelection: Schema.NullOr(ModelSelection),
  scripts: Schema.Array(ProjectScript),
  /**
   * Commands this project allows an agent to run on the *user's* machine
   * through the desktop app. Rides the same rail as `scripts` — project
   * metadata, no aggregate of its own — and decodes to `[]` for every project
   * stored before the catalog existed.
   */
  localActions: Schema.Array(ProjectLocalAction).pipe(Schema.withDecodingDefault(() => [])),
  /**
   * Projects whose repositories this project's threads may delegate work into.
   *
   * A product that lives in two repositories — an API and the mobile app that
   * calls it — is one piece of work spread over two projects, and an agent
   * opened on either half has to be able to hand the other half to a subagent.
   * The link is what makes that *possible*; it never makes it *allowed*. Every
   * cross-project delegation still checks `p.thread.create` on the destination
   * for the human who owns the delegating thread, so a link can widen what an
   * agent may reach but never what its owner may reach.
   *
   * Directed on purpose: A listing B says "threads in A may work in B", and
   * says nothing about the reverse. Two-way access is two declarations, which
   * is more typing and exactly one less thing to get wrong.
   *
   * Rides the same rail as `scripts` and `localActions` — project metadata,
   * no aggregate of its own — and decodes to `[]` for every project stored
   * before the links existed.
   */
  linkedProjectIds: Schema.Array(ProjectId).pipe(Schema.withDecodingDefault(() => [])),
  agentIds: Schema.Array(ManagedAgentId).pipe(Schema.withDecodingDefault(() => [])),
  /**
   * The branch this project's shared environments and PR previews are built
   * from. Null until an administrator names one, and null is what makes the
   * "New project environment" button say why it is disabled instead of guessing
   * a branch and building QA out of the wrong code.
   *
   * Not the same thing as `repositoryDefaultBranch`, which says where a new
   * piece of work *starts*. Two questions, the same answer only in the simplest
   * setups.
   */
  baseBranch: nullableText,
  iconFileName: Schema.optional(Schema.NullOr(Schema.String)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  color: Schema.optional(Schema.NullOr(TagColor)).pipe(Schema.withDecodingDefault(() => null)),
  description: Schema.optional(Schema.NullOr(ProjectDescription)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  isGitRepository: Schema.optional(Schema.Boolean),
  isBareRepository: Schema.optional(Schema.Boolean).pipe(Schema.withDecodingDefault(() => false)),
  ...projectRepositoryFields,
  ...projectPlaneFields,
} as const;

export const OrchestrationProject = Schema.Struct({
  ...projectBaseFields,
  deletedAt: Schema.NullOr(IsoDateTime),
});
export type OrchestrationProject = typeof OrchestrationProject.Type;

export const OrchestrationProjectShell = Schema.Struct(projectBaseFields);
export type OrchestrationProjectShell = typeof OrchestrationProjectShell.Type;

export const OrchestrationMessageRole = Schema.Literals(["user", "assistant", "system"]);
export type OrchestrationMessageRole = typeof OrchestrationMessageRole.Type;

export const OrchestrationMessage = Schema.Struct({
  id: MessageId,
  role: OrchestrationMessageRole,
  text: Schema.String,
  attachments: Schema.optional(Schema.Array(ChatAttachment)),
  skills: Schema.optional(Schema.Array(ProviderSkillReference)),
  mentions: Schema.optional(Schema.Array(ProviderMentionReference)),
  /**
   * Who wrote a `user` message, so a shared thread can attribute each turn.
   *
   * Derived from the authenticated actor of the `thread.message-sent` event
   * (`metadata.actorId`), never from anything the client sends. Absent for
   * assistant/system messages, for installs without auth, and for messages
   * written before authorship was recorded. `authorDisplayName` is resolved at
   * query time from the users table — it is a rendering convenience, not part
   * of the stored fact, so a renamed user shows their current name everywhere.
   */
  authorUserId: Schema.optional(Schema.NullOr(UserId)),
  authorDisplayName: Schema.optional(Schema.NullOr(Schema.String)),
  turnId: Schema.NullOr(TurnId),
  streaming: Schema.Boolean,
  source: OrchestrationMessageSource.pipe(Schema.withDecodingDefault(() => "native")),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OrchestrationMessage = typeof OrchestrationMessage.Type;

export const ThreadHandoff = Schema.Struct({
  sourceThreadId: ThreadId,
  sourceProvider: ProviderKind,
  importedAt: IsoDateTime,
  bootstrapStatus: ThreadHandoffBootstrapStatus,
});
export type ThreadHandoff = typeof ThreadHandoff.Type;

export const OrchestrationProposedPlanId = TrimmedNonEmptyString;
export type OrchestrationProposedPlanId = typeof OrchestrationProposedPlanId.Type;

export const OrchestrationProposedPlan = Schema.Struct({
  id: OrchestrationProposedPlanId,
  turnId: Schema.NullOr(TurnId),
  planMarkdown: TrimmedNonEmptyString,
  implementedAt: Schema.NullOr(IsoDateTime).pipe(Schema.withDecodingDefault(() => null)),
  implementationThreadId: Schema.NullOr(ThreadId).pipe(Schema.withDecodingDefault(() => null)),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type OrchestrationProposedPlan = typeof OrchestrationProposedPlan.Type;

export const SourceProposedPlanReference = Schema.Struct({
  threadId: ThreadId,
  planId: OrchestrationProposedPlanId,
});
export type SourceProposedPlanReference = typeof SourceProposedPlanReference.Type;

export const OrchestrationSessionStatus = Schema.Literals([
  "idle",
  "starting",
  "running",
  "ready",
  "interrupted",
  "stopped",
  "error",
]);
export type OrchestrationSessionStatus = typeof OrchestrationSessionStatus.Type;

export const OrchestrationSession = Schema.Struct({
  threadId: ThreadId,
  status: OrchestrationSessionStatus,
  providerName: Schema.NullOr(TrimmedNonEmptyString),
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(() => DEFAULT_RUNTIME_MODE)),
  activeTurnId: Schema.NullOr(TurnId),
  lastError: Schema.NullOr(TrimmedNonEmptyString),
  updatedAt: IsoDateTime,
});
export type OrchestrationSession = typeof OrchestrationSession.Type;

export const OrchestrationCheckpointFile = Schema.Struct({
  path: TrimmedNonEmptyString,
  kind: TrimmedNonEmptyString,
  additions: NonNegativeInt,
  deletions: NonNegativeInt,
});
export type OrchestrationCheckpointFile = typeof OrchestrationCheckpointFile.Type;

export const OrchestrationCheckpointStatus = Schema.Literals(["ready", "missing", "error"]);
export type OrchestrationCheckpointStatus = typeof OrchestrationCheckpointStatus.Type;

export const DevServerState = Schema.Literals(["idle", "booting", "running", "error"]);
export type DevServerState = typeof DevServerState.Type;

/**
 * Frameworks the dev server detector knows how to boot. Declared here rather
 * than in `@taskin-code/shared` so the id set has a single source of truth: the
 * detection logic in shared imports these, and the wire schema constrains them.
 */
export const DEV_SERVER_FRAMEWORK_IDS = [
  "laravel",
  "next",
  "nuxt",
  "astro",
  "vite",
  "rails",
  "django",
  "package-script",
] as const;

export const DevServerFrameworkId = Schema.Literals(DEV_SERVER_FRAMEWORK_IDS);
export type DevServerFrameworkId = typeof DevServerFrameworkId.Type;

/** A framework recognised in a thread's worktree, surfaced for the user to pick. */
export const DevServerDetection = Schema.Struct({
  frameworkId: DevServerFrameworkId,
  label: TrimmedNonEmptyString,
  confidence: NonNegativeInt,
  notes: Schema.Array(TrimmedNonEmptyString),
});
export type DevServerDetection = typeof DevServerDetection.Type;

export const DevServerStatus = Schema.Struct({
  state: DevServerState,
  url: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  internalPort: Schema.optional(Schema.NullOr(NonNegativeInt)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /**
   * Public preview hostname routed by the ingress, e.g.
   * `fix-login-yadel.prev.taskin-ai.net`. Replaces the Tailscale external port
   * this feature was originally scaffolded around: previews are routed by Host
   * header against our own wildcard certificate, not by per-thread TCP port.
   */
  hostname: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  frameworkId: Schema.optional(Schema.NullOr(DevServerFrameworkId)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  startedAt: Schema.optional(Schema.NullOr(IsoDateTime)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  lastActivityAt: Schema.optional(Schema.NullOr(IsoDateTime)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /**
   * When public access to this preview stops, or null while it is private.
   *
   * A timestamp rather than a boolean because the grant always expires: a
   * preview serves an unhardened dev server to the open internet, and the one
   * failure mode worth designing against is somebody publishing one and
   * forgetting. Readers compare it against the clock; nothing sweeps it.
   */
  publicUntil: Schema.optional(Schema.NullOr(IsoDateTime)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  error: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
});
export type DevServerStatus = typeof DevServerStatus.Type;

export const DEFAULT_DEV_SERVER_STATUS: DevServerStatus = {
  state: "idle",
  url: null,
  internalPort: null,
  hostname: null,
  frameworkId: null,
  startedAt: null,
  lastActivityAt: null,
  publicUntil: null,
  error: null,
};

export const OrchestrationCheckpointSummary = Schema.Struct({
  turnId: TurnId,
  checkpointTurnCount: NonNegativeInt,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.NullOr(MessageId),
  completedAt: IsoDateTime,
});
export type OrchestrationCheckpointSummary = typeof OrchestrationCheckpointSummary.Type;

export const OrchestrationThreadActivityTone = Schema.Literals([
  "info",
  "tool",
  "approval",
  "error",
]);
export type OrchestrationThreadActivityTone = typeof OrchestrationThreadActivityTone.Type;

export const OrchestrationThreadActivity = Schema.Struct({
  id: EventId,
  tone: OrchestrationThreadActivityTone,
  kind: TrimmedNonEmptyString,
  summary: TrimmedNonEmptyString,
  payload: Schema.Unknown,
  turnId: Schema.NullOr(TurnId),
  sequence: Schema.optional(NonNegativeInt),
  createdAt: IsoDateTime,
});
export type OrchestrationThreadActivity = typeof OrchestrationThreadActivity.Type;

/**
 * A delegating agent picked up one of its delegation's reports.
 *
 * Written on the *delegated* thread by `agent_delegate_poll`, once per finished
 * turn of that thread. It is the read receipt the wake reactor runs on: without
 * it, "has the parent seen this report?" could only be guessed from clocks, and
 * a parent that ended its turn without polling would look like a parent that had
 * already read — leaving the report unclaimed forever.
 */
export const DELEGATION_REPORT_READ_ACTIVITY_KIND = "delegation.report.read";

/**
 * A delegation finished and the server chose *not* to resume its parent.
 *
 * Written on the parent thread when the wake caps are reached, which is the one
 * outcome nothing else will undo on its own: the report stays unread and the
 * thread stays asleep until a human writes in it. That makes this row the only
 * evidence of a stalled orchestration, so the web treats it as a thread asking
 * for attention rather than as another line in the timeline.
 */
export const DELEGATION_WAKE_SKIPPED_ACTIVITY_KIND = "delegation.wake.skipped";

/**
 * Every compaction of a thread's context — manual (`/compact`) or provider
 * driven — is reported as a single activity of this kind. One row per
 * compaction, updated in place through its lifecycle, never one row per
 * provider event: see `ContextCompactionActivityPayload`.
 */
export const CONTEXT_COMPACTION_ACTIVITY_KIND = "context-compaction";

export const ContextCompactionStatus = Schema.Literals(["inProgress", "completed", "failed"]);
export type ContextCompactionStatus = typeof ContextCompactionStatus.Type;

/** How the compaction was triggered, when the provider tells us. */
export const ContextCompactionTrigger = Schema.Literals(["manual", "auto"]);
export type ContextCompactionTrigger = typeof ContextCompactionTrigger.Type;

/**
 * Payload of a `context-compaction` activity.
 *
 * Providers report a compaction through several events (a start, a result, a
 * boundary marker), each carrying a different slice of the truth. They are
 * folded into this one shape and stored on a single activity whose id stays
 * stable for the whole compaction, so the client renders one row that moves
 * from `inProgress` to `completed`/`failed` instead of a pile of rows.
 *
 * Everything except `status` is optional on purpose: what a provider knows
 * varies (Codex gives no token counts, Claude gives no post-compaction
 * summary text), and the UI degrades to the metadata it does have.
 */
export const ContextCompactionActivityPayload = Schema.Struct({
  status: ContextCompactionStatus,
  trigger: Schema.optional(ContextCompactionTrigger),
  /** Tokens in the context before compacting. */
  preTokens: Schema.optional(NonNegativeInt),
  /** Tokens left after compacting, when observable. */
  postTokens: Schema.optional(NonNegativeInt),
  /** The recap the agent keeps in place of the compacted history. */
  summaryText: Schema.optional(TrimmedNonEmptyString),
  /** Short human-readable status line, already truncated by the server. */
  detail: Schema.optional(TrimmedNonEmptyString),
});
export type ContextCompactionActivityPayload = typeof ContextCompactionActivityPayload.Type;

const OrchestrationLatestTurnState = Schema.Literals([
  "running",
  "interrupted",
  "completed",
  "error",
]);
export type OrchestrationLatestTurnState = typeof OrchestrationLatestTurnState.Type;

export const OrchestrationLatestTurn = Schema.Struct({
  turnId: TurnId,
  state: OrchestrationLatestTurnState,
  requestedAt: IsoDateTime,
  startedAt: Schema.NullOr(IsoDateTime),
  completedAt: Schema.NullOr(IsoDateTime),
  assistantMessageId: Schema.NullOr(MessageId),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
});
export type OrchestrationLatestTurn = typeof OrchestrationLatestTurn.Type;

export const OrchestrationThread = Schema.Struct({
  id: ThreadId,
  projectId: ProjectId,
  kind: Schema.Literals(["standard", "initiative"] as const).pipe(
    Schema.withDecodingDefault(() => "standard"),
  ),
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(() => DEFAULT_PROVIDER_INTERACTION_MODE),
  ),
  envMode: Schema.optional(ThreadEnvironmentMode).pipe(Schema.withDecodingDefault(() => "local")),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  /**
   * Environment whose workspace this thread borrows, when it has one.
   *
   * Null for the ordinary case: the thread owns its worktree and loses it when
   * archived. Non-null makes the thread a *guest* — its cwd, its preview and
   * its sandbox database belong to the environment, several threads share them
   * at once, and archiving the thread destroys none of it.
   */
  environmentId: Schema.optional(Schema.NullOr(EnvironmentId)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  associatedWorktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  associatedWorktreeBranch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  associatedWorktreeRef: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  associatedAgentId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  parentThreadId: Schema.optional(Schema.NullOr(ThreadId)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  subagentAgentId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  subagentNickname: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  subagentRole: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  forkSourceThreadId: Schema.optional(Schema.NullOr(ThreadId)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /**
   * User who created the thread, and therefore its owner.
   *
   * Null for threads created before authorship was recorded and on installs
   * without auth. A thread with no owner is visible only to administrators:
   * every command that creates a thread has to name its author (the dispatch
   * signature demands it), so a null here means the row predates that rule, and
   * treating it as everyone's would publish one person's work to a whole
   * project. Like [[assignedUserIds]] it is projected from the event's actor,
   * never accepted from a client.
   */
  createdBy: Schema.optional(Schema.NullOr(UserId)).pipe(Schema.withDecodingDefault(() => null)),
  /**
   * Users the thread is shared with, on top of its creator.
   *
   * Membership is what makes a thread visible to someone who did not create
   * it, so this list is server-authoritative: it is projected from
   * `thread.user-assigned` / `thread.user-unassigned` events and is never
   * accepted from a client. Empty on installs without auth.
   */
  assignedUserIds: Schema.optional(Schema.Array(UserId)).pipe(Schema.withDecodingDefault(() => [])),
  latestTurn: Schema.NullOr(OrchestrationLatestTurn),
  latestUserMessageAt: Schema.optional(Schema.NullOr(IsoDateTime)),
  hasPendingApprovals: Schema.optional(Schema.Boolean),
  hasPendingUserInput: Schema.optional(Schema.Boolean),
  hasActionableProposedPlan: Schema.optional(Schema.Boolean),
  /**
   * When this thread's parent last picked up one of its reports, derived from
   * the newest `delegation.report.read` activity. Absent on threads nobody has
   * ever read — including every thread that is not a delegation, which is why it
   * is optional rather than defaulted: "no receipt" and "not applicable" are the
   * same answer here, and `hasUnreadDelegateReport` reads both as unread.
   */
  delegateReportReadAt: Schema.optional(Schema.NullOr(IsoDateTime)),
  /**
   * When the server last decided it would not resume this thread on its own even
   * though a delegation was owed a read, from the newest
   * `delegation.wake.skipped` activity. Absent on every thread the server never
   * gave up on, which is almost all of them.
   */
  delegationWakeSkippedAt: Schema.optional(Schema.NullOr(IsoDateTime)),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  archivedAt: Schema.optional(Schema.NullOr(IsoDateTime)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  deletedAt: Schema.NullOr(IsoDateTime),
  handoff: Schema.NullOr(ThreadHandoff).pipe(Schema.withDecodingDefault(() => null)),
  /**
   * Rendering cut-off for the conversation view. Messages, plans and
   * activities created strictly before this instant are never shown again —
   * they stay in the event log and in the projections, but the UI must not
   * offer any way back to them. Null when the thread was never cleared.
   */
  contextClearedAt: Schema.optional(Schema.NullOr(IsoDateTime)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  messages: Schema.Array(OrchestrationMessage),
  proposedPlans: Schema.Array(OrchestrationProposedPlan).pipe(Schema.withDecodingDefault(() => [])),
  activities: Schema.Array(OrchestrationThreadActivity),
  checkpoints: Schema.Array(OrchestrationCheckpointSummary),
  session: Schema.NullOr(OrchestrationSession),
  /**
   * Plane issue this thread is working, if any. Both halves travel together so
   * the UI can render the ref and act on the id without another round trip.
   */
  planeIssueRef: Schema.optional(Schema.NullOr(PlaneIssueRef)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  planeIssueId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /**
   * Pull request this thread is working, if any.
   *
   * Only the address is kept — project and number — because everything else
   * about a pull request (title, state, checks, counts) is read live from
   * GitHub. Copying any of it here would put a stale "open" badge on a thread
   * whose branch was merged an hour ago, and the projection has no way to learn
   * otherwise: nothing on GitHub's side writes to this event log.
   */
  linkedPullRequest: Schema.optional(Schema.NullOr(PullRequestRef)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  devCommandOverride: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  devServerStatus: Schema.optional(DevServerStatus).pipe(
    Schema.withDecodingDefault(() => DEFAULT_DEV_SERVER_STATUS),
  ),
});
export type OrchestrationThread = typeof OrchestrationThread.Type;

/**
 * A thread's recap as the client sees it: the sentence and when it was written.
 *
 * `sourceTurnId` and `model` stay server-side — the browser has nothing to do
 * with either, and they exist for idempotence and for debugging a bad
 * sentence, not for rendering.
 */
export const OrchestrationThreadRecap = Schema.Struct({
  text: TrimmedNonEmptyString,
  generatedAt: IsoDateTime,
});
export type OrchestrationThreadRecap = typeof OrchestrationThreadRecap.Type;

export const OrchestrationThreadShell = Schema.Struct({
  id: ThreadId,
  projectId: ProjectId,
  /** Thread kind is carried on the shell because the focused sidebar renders from shells. */
  kind: Schema.optional(Schema.Literals(["standard", "initiative"] as const)),
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(() => DEFAULT_PROVIDER_INTERACTION_MODE),
  ),
  envMode: Schema.optional(ThreadEnvironmentMode).pipe(Schema.withDecodingDefault(() => "local")),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  /** See `OrchestrationThread.environmentId`. Carried on the shell too, because
   * the header badge has to be right on first paint: arriving a beat later would
   * make a shared workspace look like an ordinary one for the moment that
   * matters most, when the person is deciding what this thread is. */
  environmentId: Schema.optional(Schema.NullOr(EnvironmentId)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  associatedWorktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  associatedWorktreeBranch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  associatedWorktreeRef: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  associatedAgentId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  parentThreadId: Schema.optional(Schema.NullOr(ThreadId)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  subagentAgentId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  subagentNickname: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  subagentRole: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  forkSourceThreadId: Schema.optional(Schema.NullOr(ThreadId)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /**
   * User who created the thread, and therefore its owner.
   *
   * Null for threads created before authorship was recorded and on installs
   * without auth. A thread with no owner is visible only to administrators:
   * every command that creates a thread has to name its author (the dispatch
   * signature demands it), so a null here means the row predates that rule, and
   * treating it as everyone's would publish one person's work to a whole
   * project. Like [[assignedUserIds]] it is projected from the event's actor,
   * never accepted from a client.
   */
  createdBy: Schema.optional(Schema.NullOr(UserId)).pipe(Schema.withDecodingDefault(() => null)),
  /**
   * Users the thread is shared with, on top of its creator.
   *
   * Membership is what makes a thread visible to someone who did not create
   * it, so this list is server-authoritative: it is projected from
   * `thread.user-assigned` / `thread.user-unassigned` events and is never
   * accepted from a client. Empty on installs without auth.
   */
  assignedUserIds: Schema.optional(Schema.Array(UserId)).pipe(Schema.withDecodingDefault(() => [])),
  latestTurn: Schema.NullOr(OrchestrationLatestTurn),
  latestUserMessageAt: Schema.optional(Schema.NullOr(IsoDateTime)),
  hasPendingApprovals: Schema.optional(Schema.Boolean),
  hasPendingUserInput: Schema.optional(Schema.Boolean),
  hasActionableProposedPlan: Schema.optional(Schema.Boolean),
  /**
   * Same read receipt as `OrchestrationThread.delegateReportReadAt`. It travels
   * on the shell because the sidebar paints "Delegate Ready" from shells alone:
   * without it the UI would keep announcing a report the server already
   * considers picked up.
   */
  delegateReportReadAt: Schema.optional(Schema.NullOr(IsoDateTime)),
  /**
   * Same decision as `OrchestrationThread.delegationWakeSkippedAt`. On the shell
   * because it is what the sidebar's delegation badge is judged by, and that
   * badge has to be right for threads the user has never opened.
   */
  delegationWakeSkippedAt: Schema.optional(Schema.NullOr(IsoDateTime)),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  archivedAt: Schema.optional(Schema.NullOr(IsoDateTime)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /**
   * Same cut-off as `OrchestrationThread.contextClearedAt`. It has to travel on
   * the shell too: the client rebuilds a thread on top of its shell, so a shell
   * refresh arriving after the detail snapshot would otherwise drop the cut-off
   * and put the hidden conversation back on screen.
   */
  contextClearedAt: Schema.optional(Schema.NullOr(IsoDateTime)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  handoff: Schema.NullOr(ThreadHandoff).pipe(Schema.withDecodingDefault(() => null)),
  session: Schema.NullOr(OrchestrationSession),
  /**
   * Plane issue this thread is working, if any. Both halves travel together so
   * the UI can render the ref and act on the id without another round trip.
   */
  planeIssueRef: Schema.optional(Schema.NullOr(PlaneIssueRef)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  planeIssueId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /**
   * Pull request this thread is working, if any.
   *
   * Only the address is kept — project and number — because everything else
   * about a pull request (title, state, checks, counts) is read live from
   * GitHub. Copying any of it here would put a stale "open" badge on a thread
   * whose branch was merged an hour ago, and the projection has no way to learn
   * otherwise: nothing on GitHub's side writes to this event log.
   */
  linkedPullRequest: Schema.optional(Schema.NullOr(PullRequestRef)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  devCommandOverride: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  devServerStatus: Schema.optional(DevServerStatus).pipe(
    Schema.withDecodingDefault(() => DEFAULT_DEV_SERVER_STATUS),
  ),
  /**
   * The thread's recap, when it has one.
   *
   * It rides the shared shell rather than the per-person `ThreadUserState`
   * channel because it is not per-person: one sentence, the same for everyone
   * who can see the thread, computed once for every connection. Null on an
   * installation with recaps off, or on a thread that has not earned one.
   */
  recap: Schema.optional(Schema.NullOr(OrchestrationThreadRecap)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
});
export type OrchestrationThreadShell = typeof OrchestrationThreadShell.Type;

export const OrchestrationReadModel = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  projects: Schema.Array(OrchestrationProject),
  threads: Schema.Array(OrchestrationThread),
  /**
   * Live and destroyed environments, needed by the decider to enforce
   * one-per-`(user, project)` and to refuse threads pointing at an environment
   * that is gone.
   *
   * Optional rather than defaulted: every caller that builds a read model by
   * hand (the engine's empty state, a few dozen tests) predates environments
   * and means "none", and a required field would force each of them to say so.
   */
  environments: Schema.optional(Schema.Array(ProjectEnvironment)),
  /**
   * Live and destroyed previews, needed by the decider to enforce one live
   * preview per `(project, pull request)` and to refuse commands against a
   * preview that is already gone.
   *
   * Optional for the same reason as `environments`: every read model built by
   * hand predates previews and means "none".
   */
  prPreviews: Schema.optional(Schema.Array(PrPreview)),
  updatedAt: IsoDateTime,
});
export type OrchestrationReadModel = typeof OrchestrationReadModel.Type;

export const OrchestrationShellSnapshot = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  projects: Schema.Array(OrchestrationProjectShell),
  threads: Schema.Array(OrchestrationThreadShell),
  updatedAt: IsoDateTime,
});
export type OrchestrationShellSnapshot = typeof OrchestrationShellSnapshot.Type;

export const OrchestrationShellStreamEvent = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("project-upserted"),
    sequence: NonNegativeInt,
    project: OrchestrationProjectShell,
  }),
  Schema.Struct({
    kind: Schema.Literal("project-removed"),
    sequence: NonNegativeInt,
    projectId: ProjectId,
  }),
  Schema.Struct({
    kind: Schema.Literal("thread-upserted"),
    sequence: NonNegativeInt,
    thread: OrchestrationThreadShell,
  }),
  Schema.Struct({
    kind: Schema.Literal("thread-removed"),
    sequence: NonNegativeInt,
    threadId: ThreadId,
  }),
]);
export type OrchestrationShellStreamEvent = typeof OrchestrationShellStreamEvent.Type;

export const OrchestrationShellStreamItem = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("snapshot"),
    snapshot: OrchestrationShellSnapshot,
  }),
  OrchestrationShellStreamEvent,
]);
export type OrchestrationShellStreamItem = typeof OrchestrationShellStreamItem.Type;

export const ProjectCreateCommand = Schema.Struct({
  type: Schema.Literal("project.create"),
  commandId: CommandId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
  createdAt: IsoDateTime,
});

const ProjectMetaUpdateCommand = Schema.Struct({
  type: Schema.Literal("project.meta.update"),
  commandId: CommandId,
  projectId: ProjectId,
  title: Schema.optional(TrimmedNonEmptyString),
  workspaceRoot: Schema.optional(TrimmedNonEmptyString),
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
  scripts: Schema.optional(Schema.Array(ProjectScript)),
  /**
   * Absent means "leave the catalog alone"; present replaces it wholesale, the
   * same way `scripts` travels. Carrying it requires `p.local.manage` on top of
   * `p.edit` — see `commandAuthorization.ts`.
   *
   * Each entry's `isTemplate` is ignored here: the decider carries the stored
   * value forward by id (new entries start `false`), so an ordinary edit can
   * neither publish a command into every other project's "Añadir comando" nor
   * silently unpublish one by omitting the field. Only
   * `project.localAction.template.set` moves it.
   */
  localActions: Schema.optional(Schema.Array(ProjectLocalAction)),
  /**
   * Absent means "leave the links alone"; present replaces them wholesale, the
   * same way `scripts` travels. Requires `p.edit` like the rest of the project
   * metadata — the destination's own permissions are what gate the delegation
   * itself, so declaring a link grants nobody anything on their own.
   */
  linkedProjectIds: Schema.optional(Schema.Array(ProjectId)),
  agentIds: Schema.optional(Schema.Array(ManagedAgentId)),
  /** Server-only. The WebSocket dispatch path refuses client-supplied values. */
  iconFileName: Schema.optional(Schema.NullOr(Schema.String)),
  color: Schema.optional(Schema.NullOr(TagColor)),
  // The decider trims this and turns an empty value into null before emitting.
  description: Schema.optional(
    Schema.NullOr(Schema.String.check(Schema.isMaxLength(PROJECT_DESCRIPTION_MAX_LENGTH))),
  ),
  /**
   * Absent means "leave it alone"; `null` clears it. Gated by `p.edit` like the
   * rest of the project metadata — it decides where shared environments come
   * from, not who may reach them.
   *
   * It leaves the decider as its own event (`project.base-branch-changed`)
   * rather than as a field of `project.meta-updated`: it is the one piece of
   * project metadata other aggregates react to, and a reader that has to scan
   * every metadata update to notice it would eventually miss one.
   */
  baseBranch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
});

/**
 * Publish or unpublish one local command as a template for other projects.
 *
 * Its own command rather than a field on the update above, because the two
 * carry different authority: editing the catalog is `p.local.manage` on this
 * project, publishing out of it is the global `p.local.template`. Keeping them
 * apart is what lets the decider treat `isTemplate` on `project.meta.update` as
 * "not yours to set" without having to diff arrays to find out who moved what.
 */
const ProjectLocalActionTemplateSetCommand = Schema.Struct({
  type: Schema.Literal("project.localAction.template.set"),
  commandId: CommandId,
  projectId: ProjectId,
  actionId: LocalActionId,
  isTemplate: Schema.Boolean,
});

const ProjectDeleteCommand = Schema.Struct({
  type: Schema.Literal("project.delete"),
  commandId: CommandId,
  projectId: ProjectId,
});

/**
 * Which environment operation a thread was opened to carry out.
 *
 * Seven, because they are the seven buttons: bringing the environment onto its
 * base branch, putting its data back, checking a freshly cloned one, trying a
 * pull request on it, taking that pull request back off again, and — F4 — the
 * two halves of an *isolated* preview, which builds a pull request its own
 * workspace instead of borrowing the shared one.
 *
 * They share one enforcement rule — the agent must carry the three QA tools
 * every environment operation needs — and the last four add their own on top
 * (`qa_try_pr`, `qa_back_to_base`, `qa_preview_create`, `qa_preview_destroy`),
 * because an agent sent to try a pull request without the tool that tries it
 * can only stop and apologise. See `managedAgentQaCapability.ts`.
 */
export const QaOperationKind = Schema.Literals([
  "sync",
  "reset-data",
  "clone",
  "try-pr",
  "back-to-base",
  "preview-create",
  "preview-destroy",
]);
export type QaOperationKind = typeof QaOperationKind.Type;

const ThreadCreateCommand = Schema.Struct({
  type: Schema.Literal("thread.create"),
  commandId: CommandId,
  threadId: ThreadId,
  projectId: ProjectId,
  kind: Schema.optional(Schema.Literals(["standard", "initiative"] as const)).pipe(
    Schema.withDecodingDefault(() => "standard"),
  ),
  title: TrimmedNonEmptyString,
  // Optional: when omitted, the server resolves project default → user default
  // → system default. The server still validates the resolved choice against
  // the user's allowlist before persisting.
  modelSelection: Schema.optional(ModelSelection),
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(() => DEFAULT_PROVIDER_INTERACTION_MODE),
  ),
  envMode: Schema.optional(ThreadEnvironmentMode).pipe(Schema.withDecodingDefault(() => "local")),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  /**
   * Environment to run this thread in. When set, the server takes the cwd from
   * the environment and does not create a worktree for the thread.
   *
   * Three values, not two: an id picks that environment, an explicit `null`
   * says "no environment, an ordinary worktree", and *absent* asks the server
   * to decide (`environmentResolution`). The distinction is what lets the
   * New-thread dialog offer «Ninguno» — without it, choosing "none" would be
   * indistinguishable from not choosing, and the server would silently put the
   * thread in an environment anyway. Clients written before the selector
   * existed omit the field and keep the old behaviour.
   */
  environmentId: Schema.optional(Schema.NullOr(EnvironmentId)),
  associatedWorktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  associatedWorktreeBranch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  associatedWorktreeRef: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  associatedAgentId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  parentThreadId: Schema.optional(Schema.NullOr(ThreadId)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  subagentAgentId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  subagentNickname: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  subagentRole: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /**
   * Plane issue the thread is being opened for. Both halves or neither, same
   * rule as `thread.plane-issue.set` — see that command for why.
   *
   * Creating a thread from an issue produces two events, `thread.created`
   * followed by `thread.plane-issue-linked`, rather than folding the link into
   * the creation payload: the link can also be set and cleared later, and one
   * writer for one fact is what keeps the projection honest.
   */
  planeIssueRef: Schema.optional(Schema.NullOr(PlaneIssueRef)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  planeIssueId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /**
   * Set when this thread is being opened by an environment card's button to
   * have an agent operate the environment.
   *
   * It exists so the *server* can enforce what the picker offered: an agent
   * without `qa_env_preflight`, `qa_env_sync` and `qa_env_config_set` is
   * refused here, however the command was assembled. Without the mark the
   * server could not tell "open a thread in qa and chat" — which any agent may
   * do — from "go and sync qa", which only a capable one may be sent to.
   *
   * Command-only: it is a fact about how the thread was started, not about what
   * the thread *is*, and the conversation itself is the record of the operation.
   */
  qaOperation: Schema.optional(Schema.NullOr(QaOperationKind)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  createdAt: IsoDateTime,
});

export const ThreadHandoffImportedMessage = Schema.Struct({
  messageId: MessageId,
  role: Schema.Literals(["user", "assistant"]),
  text: Schema.String,
  attachments: Schema.optional(Schema.Array(ChatAttachment)),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type ThreadHandoffImportedMessage = typeof ThreadHandoffImportedMessage.Type;

const ThreadHandoffCreateCommand = Schema.Struct({
  type: Schema.Literal("thread.handoff.create"),
  commandId: CommandId,
  threadId: ThreadId,
  sourceThreadId: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(() => DEFAULT_PROVIDER_INTERACTION_MODE),
  ),
  envMode: Schema.optional(ThreadEnvironmentMode).pipe(Schema.withDecodingDefault(() => "local")),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  associatedWorktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  associatedWorktreeBranch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  associatedWorktreeRef: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  importedMessages: Schema.Array(ThreadHandoffImportedMessage),
  createdAt: IsoDateTime,
});

const ThreadForkCreateCommand = Schema.Struct({
  type: Schema.Literal("thread.fork.create"),
  commandId: CommandId,
  threadId: ThreadId,
  sourceThreadId: ThreadId,
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(() => DEFAULT_PROVIDER_INTERACTION_MODE),
  ),
  envMode: Schema.optional(ThreadEnvironmentMode).pipe(Schema.withDecodingDefault(() => "local")),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  associatedWorktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  associatedWorktreeBranch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  associatedWorktreeRef: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  importedMessages: Schema.Array(ThreadHandoffImportedMessage),
  createdAt: IsoDateTime,
});

const ThreadDeleteCommand = Schema.Struct({
  type: Schema.Literal("thread.delete"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadArchiveCommand = Schema.Struct({
  type: Schema.Literal("thread.archive"),
  commandId: CommandId,
  threadId: ThreadId,
});

const ThreadUnarchiveCommand = Schema.Struct({
  type: Schema.Literal("thread.unarchive"),
  commandId: CommandId,
  threadId: ThreadId,
});

/**
 * Mark a thread done, or undo that, for one person.
 *
 * `userId` is on the command rather than read from `metadata.actorId` because
 * the snooze sweep raises the same command on someone's behalf, with no
 * connection and no actor behind it. It is never trusted from the wire: the
 * dispatcher overwrites it with the authenticated user of the connection, and
 * refuses the command when there is none — this state has to belong to
 * somebody, and guessing an owner is worse than failing.
 *
 * The client sends an intent, not a timestamp. Two clients toggling at once
 * then converge on the server's clock instead of racing on their own.
 */
const ThreadSettleCommand = Schema.Struct({
  type: Schema.Literal("thread.settle"),
  commandId: CommandId,
  threadId: ThreadId,
  userId: UserId,
  isSettled: Schema.Boolean,
});

/**
 * Replace a thread's recap: one line saying where the thread stands.
 *
 * Server-only, and deliberately absent from the client command union — the
 * text is written by the recap reactor after a turn settles, never by a
 * browser. `sourceTurnId` travels with it so the projection records which turn
 * the sentence describes; the reactor reads it back to recognise a turn it has
 * already covered, since a settled turn is announced more than once.
 */
const ThreadRecapSetCommand = Schema.Struct({
  type: Schema.Literal("thread.recap.set"),
  commandId: CommandId,
  threadId: ThreadId,
  text: TrimmedNonEmptyString,
  sourceTurnId: Schema.NullOr(TurnId),
  model: Schema.NullOr(TrimmedNonEmptyString),
});

/**
 * Put a thread out of one person's inbox until an instant, or cancel that.
 *
 * `snoozeUntil` is null to cancel, which is also how the sweep wakes a thread
 * whose deadline passed. An absolute instant, resolved by whoever offered the
 * "tomorrow morning" choice: the server must not reinterpret a preset, since it
 * does not know the reader's timezone.
 */
const ThreadSnoozeCommand = Schema.Struct({
  type: Schema.Literal("thread.snooze"),
  commandId: CommandId,
  threadId: ThreadId,
  userId: UserId,
  snoozeUntil: Schema.NullOr(IsoDateTime),
  /**
   * Set only by the wake sweep, to say the deadline arrived rather than that
   * somebody clicked. It is what earns the thread a "volvió de pospuesto" mark:
   * a person who just brought it back by hand does not need telling.
   *
   * Absent from the client command, like `userId`, so it cannot be claimed from
   * the wire.
   */
  wokenOnSchedule: Schema.optional(Schema.Boolean),
});

/**
 * What a client is allowed to send: the same two commands without `userId`.
 *
 * Omitted rather than ignored. A field the wire may carry and the server always
 * discards reads, to the next person, like something the client gets to choose;
 * leaving it out of the schema means a client that tries to settle a thread on
 * someone else's behalf is rejected by the decoder, not by a convention.
 */
const ClientThreadSettleCommand = Schema.Struct({
  type: Schema.Literal("thread.settle"),
  commandId: CommandId,
  threadId: ThreadId,
  isSettled: Schema.Boolean,
});

const ClientThreadSnoozeCommand = Schema.Struct({
  type: Schema.Literal("thread.snooze"),
  commandId: CommandId,
  threadId: ThreadId,
  snoozeUntil: Schema.NullOr(IsoDateTime),
});

const ThreadMetaUpdateCommand = Schema.Struct({
  type: Schema.Literal("thread.meta.update"),
  commandId: CommandId,
  threadId: ThreadId,
  title: Schema.optional(TrimmedNonEmptyString),
  modelSelection: Schema.optional(ModelSelection),
  envMode: Schema.optional(ThreadEnvironmentMode),
  branch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  worktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  associatedWorktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  associatedWorktreeBranch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  associatedWorktreeRef: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  parentThreadId: Schema.optional(Schema.NullOr(ThreadId)),
  subagentAgentId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  subagentNickname: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  subagentRole: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  handoff: Schema.optional(Schema.NullOr(ThreadHandoff)),
});

const ThreadRuntimeModeSetCommand = Schema.Struct({
  type: Schema.Literal("thread.runtime-mode.set"),
  commandId: CommandId,
  threadId: ThreadId,
  runtimeMode: RuntimeMode,
  createdAt: IsoDateTime,
});

const ThreadInteractionModeSetCommand = Schema.Struct({
  type: Schema.Literal("thread.interaction-mode.set"),
  commandId: CommandId,
  threadId: ThreadId,
  interactionMode: ProviderInteractionMode,
  createdAt: IsoDateTime,
});

export const ThreadTurnStartCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.start"),
  commandId: CommandId,
  threadId: ThreadId,
  message: Schema.Struct({
    messageId: MessageId,
    /**
     * `system` is how the server itself starts a turn — today, waking a
     * delegating agent whose subagent finished (`DelegationWakeReactor`). The
     * role never reaches the provider (only the text does); it exists so the
     * transcript can show who really spoke instead of attributing the turn to
     * the user.
     *
     * `ClientThreadTurnStartCommand` deliberately stays `user`-only: starting a
     * system turn is a server capability, never something a browser can ask for.
     */
    role: Schema.Literals(["user", "system"]),
    text: Schema.String,
    attachments: Schema.Array(ChatAttachment),
    skills: Schema.optional(Schema.Array(ProviderSkillReference)),
    mentions: Schema.optional(Schema.Array(ProviderMentionReference)),
  }),
  modelSelection: Schema.optional(ModelSelection),
  providerOptions: Schema.optional(ProviderStartOptions),
  reviewTarget: Schema.optional(ProviderReviewTarget),
  assistantDeliveryMode: Schema.optional(AssistantDeliveryMode),
  dispatchMode: Schema.optional(TurnDispatchMode).pipe(
    Schema.withDecodingDefault(() => DEFAULT_TURN_DISPATCH_MODE),
  ),
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(() => DEFAULT_RUNTIME_MODE)),
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(() => DEFAULT_PROVIDER_INTERACTION_MODE),
  ),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  createdAt: IsoDateTime,
});

const ClientThreadTurnStartCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.start"),
  commandId: CommandId,
  threadId: ThreadId,
  message: Schema.Struct({
    messageId: MessageId,
    role: Schema.Literal("user"),
    text: Schema.String,
    attachments: UploadChatAttachments,
    skills: Schema.optional(Schema.Array(ProviderSkillReference)),
    mentions: Schema.optional(Schema.Array(ProviderMentionReference)),
  }),
  modelSelection: Schema.optional(ModelSelection),
  providerOptions: Schema.optional(ProviderStartOptions),
  reviewTarget: Schema.optional(ProviderReviewTarget),
  assistantDeliveryMode: Schema.optional(AssistantDeliveryMode),
  dispatchMode: Schema.optional(TurnDispatchMode).pipe(
    Schema.withDecodingDefault(() => DEFAULT_TURN_DISPATCH_MODE),
  ),
  runtimeMode: RuntimeMode,
  interactionMode: ProviderInteractionMode,
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  createdAt: IsoDateTime,
});

const ThreadTurnInterruptCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.interrupt"),
  commandId: CommandId,
  threadId: ThreadId,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

const ThreadDispatchQueuedTurnCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.dispatch-queued"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  modelSelection: Schema.optional(ModelSelection),
  providerOptions: Schema.optional(ProviderStartOptions),
  reviewTarget: Schema.optional(ProviderReviewTarget),
  assistantDeliveryMode: Schema.optional(AssistantDeliveryMode),
  dispatchMode: Schema.optional(TurnDispatchMode).pipe(
    Schema.withDecodingDefault(() => DEFAULT_TURN_DISPATCH_MODE),
  ),
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(() => DEFAULT_RUNTIME_MODE)),
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(() => DEFAULT_PROVIDER_INTERACTION_MODE),
  ),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  createdAt: IsoDateTime,
});

const ThreadApprovalRespondCommand = Schema.Struct({
  type: Schema.Literal("thread.approval.respond"),
  commandId: CommandId,
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  decision: ProviderApprovalDecision,
  createdAt: IsoDateTime,
});

const ThreadUserInputRespondCommand = Schema.Struct({
  type: Schema.Literal("thread.user-input.respond"),
  commandId: CommandId,
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  answers: ProviderUserInputAnswers,
  createdAt: IsoDateTime,
});

const ThreadCheckpointRevertCommand = Schema.Struct({
  type: Schema.Literal("thread.checkpoint.revert"),
  commandId: CommandId,
  threadId: ThreadId,
  turnCount: NonNegativeInt,
  createdAt: IsoDateTime,
});

const ThreadSessionStopCommand = Schema.Struct({
  type: Schema.Literal("thread.session.stop"),
  commandId: CommandId,
  threadId: ThreadId,
  createdAt: IsoDateTime,
});

/**
 * Clears the conversation view without creating a new thread: the worktree,
 * the branch and the event log all stay as they are, and only the rendering
 * cut-off moves forward. The provider session is reset separately, so the next
 * turn starts the model with no prior context but the same working directory.
 */
const ThreadContextClearCommand = Schema.Struct({
  type: Schema.Literal("thread.context.clear"),
  commandId: CommandId,
  threadId: ThreadId,
  createdAt: IsoDateTime,
});

/**
 * Pins the command used to boot this thread's dev server, overriding framework
 * detection. Null clears the override and returns the thread to detection.
 */
const ThreadDevCommandSetCommand = Schema.Struct({
  type: Schema.Literal("thread.dev-command.set"),
  commandId: CommandId,
  threadId: ThreadId,
  devCommand: Schema.NullOr(TrimmedNonEmptyString),
  createdAt: IsoDateTime,
});

/**
 * Points a thread at the Plane issue it is working, or clears the link.
 *
 * Both halves travel together: the readable reference (`1GAME-2`) is what a
 * person and an agent quote, and the UUID is what the API needs. Sending one
 * without the other is rejected — a thread that knows the ref but not the id
 * would have to re-resolve it on every call, and one that knows only the id
 * cannot say which issue it means.
 *
 * The link is descriptive, never a grant: the issue must belong to the board
 * the thread's own project is linked to, and that is checked server-side
 * against the projection, not against anything the client claims.
 */
const ThreadPlaneIssueSetCommand = Schema.Struct({
  type: Schema.Literal("thread.plane-issue.set"),
  commandId: CommandId,
  threadId: ThreadId,
  planeIssueRef: Schema.NullOr(PlaneIssueRef),
  planeIssueId: Schema.NullOr(TrimmedNonEmptyString),
  createdAt: IsoDateTime,
});

/**
 * Records which pull request a thread produced, or clears it with `null`.
 *
 * Its own command rather than a field on `thread.create`, for the reason
 * `thread.plane-issue.set` gives: the link is set long after the thread exists
 * — the pull request is usually opened mid-conversation — and one writer for
 * one fact is what keeps the projection honest.
 *
 * Descriptive, never a grant. Storing the link gives nobody a way to read or
 * act on that pull request: every `pullRequests.*` call re-checks `p.git_read`
 * against the project it names and resolves the repository server-side.
 */
const ThreadPullRequestSetCommand = Schema.Struct({
  type: Schema.Literal("thread.pull-request.set"),
  commandId: CommandId,
  threadId: ThreadId,
  pullRequest: Schema.NullOr(PullRequestRef),
  createdAt: IsoDateTime,
});

/**
 * Shares a thread with another user. Idempotent by design: re-assigning an
 * already assigned user is rejected by the decider rather than duplicating the
 * membership, so a double click cannot produce two rows.
 */
const ThreadAssignUserCommand = Schema.Struct({
  type: Schema.Literal("thread.assign-user"),
  commandId: CommandId,
  threadId: ThreadId,
  userId: UserId,
});

/** Revokes a thread membership granted by `thread.assign-user`. */
const ThreadUnassignUserCommand = Schema.Struct({
  type: Schema.Literal("thread.unassign-user"),
  commandId: CommandId,
  threadId: ThreadId,
  userId: UserId,
});

/**
 * Claims a preview hostname and loopback port for a thread. Server-internal:
 * the DevServerManager emits it once the process is actually listening, never
 * the client. Persisting the allocation is what makes the port sticky across
 * restarts, so a shared preview URL keeps working after a server bounce.
 */
const ThreadDevServerAllocateCommand = Schema.Struct({
  type: Schema.Literal("thread.dev-server.allocate"),
  commandId: CommandId,
  threadId: ThreadId,
  hostname: TrimmedNonEmptyString,
  port: NonNegativeInt,
  frameworkId: Schema.NullOr(DevServerFrameworkId),
  createdAt: IsoDateTime,
});

/**
 * Opens (or closes) a thread's preview to callers with no Taskin session.
 *
 * Server-internal like the allocation commands: the client asks the
 * DevServerManager, which resolves whether the preview belongs to the thread or
 * to the environment it borrows, and dispatches the right one. `publicUntil` is
 * computed server-side — a client that could name its own expiry could name one
 * in the next century.
 */
const ThreadDevServerSetVisibilityCommand = Schema.Struct({
  type: Schema.Literal("thread.dev-server.set-visibility"),
  commandId: CommandId,
  threadId: ThreadId,
  publicUntil: Schema.NullOr(IsoDateTime),
  createdAt: IsoDateTime,
});

/** Releases the hostname and port so another thread can claim them. */
const ThreadDevServerReleaseCommand = Schema.Struct({
  type: Schema.Literal("thread.dev-server.release"),
  commandId: CommandId,
  threadId: ThreadId,
  createdAt: IsoDateTime,
});

/**
 * Commands of the `environment` aggregate.
 *
 * All four are internal: provisioning creates a worktree and runs the project's
 * setup hook, so the decision of *whether* it may happen belongs to the admin
 * HTTP route, and what reaches the log is the record of work the server already
 * committed to. The client never dispatches these.
 *
 * Provisioning is split in three commands because it is slow — the setup hook
 * can run for minutes — and a single terminal command would leave the admin
 * staring at nothing, with a half-built worktree invisible to the system if the
 * server died mid-way. `environment.provision` claims the pair and records the
 * paths *before* any I/O; `.complete` and `.fail` close it.
 */
const EnvironmentProvisionCommand = Schema.Struct({
  type: Schema.Literal("environment.provision"),
  commandId: CommandId,
  environmentId: EnvironmentId,
  projectId: ProjectId,
  /** Null if and only if `kind` is `project`. Enforced by the decider. */
  userId: Schema.NullOr(UserId),
  kind: ProjectEnvironmentKind.pipe(
    Schema.withDecodingDefault(() => DEFAULT_PROJECT_ENVIRONMENT_KIND),
  ),
  /** The slug of a project environment. Required for `project`, absent otherwise. */
  name: Schema.optional(Schema.NullOr(ProjectEnvironmentName)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /**
   * The branch a project environment follows, copied from `project.baseBranch`
   * at creation time. Kept on the row rather than re-read from the project, so
   * that changing the project's base branch does not move existing environments
   * under the people working in them.
   */
  trackedBranch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  /** What this environment was copied from, when it is a clone of another. */
  clonedFrom: Schema.optional(Schema.NullOr(EnvironmentCloneOrigin)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  branch: TrimmedNonEmptyString,
  worktreePath: TrimmedNonEmptyString,
  baseRef: TrimmedNonEmptyString,
  createdAt: IsoDateTime,
});

/**
 * Records what `git` says about an environment's freshness.
 *
 * Internal, and emitted from three places that must all agree: the periodic
 * sweep, the end of any code operation, and the tools that report status. The
 * state is persisted rather than computed per reader so that the card, the
 * agent and the sweep cannot each hold a different opinion of the same worktree.
 */
const EnvironmentSyncStateSetCommand = Schema.Struct({
  type: Schema.Literal("environment.sync-state.set"),
  commandId: CommandId,
  environmentId: EnvironmentId,
  syncState: ProjectEnvironmentSyncState,
  /** Null whenever the count is not known — which is every `unknown`. */
  behindBy: Schema.NullOr(NonNegativeInt),
  checkedAt: IsoDateTime,
  createdAt: IsoDateTime,
});

/**
 * Records a successful move onto the tracked branch.
 *
 * Separate from the state above because the two answer different questions and
 * change on different clocks: "how does it stand right now?" is recomputed all
 * day, "when was it last brought up to date, and by whom?" is the audit trail
 * and must not be overwritten by a sweep that merely looked.
 */
const EnvironmentSyncRecordCommand = Schema.Struct({
  type: Schema.Literal("environment.sync.record"),
  commandId: CommandId,
  environmentId: EnvironmentId,
  syncedSha: TrimmedNonEmptyString,
  /** Null when the server did it on its own behalf rather than for somebody. */
  syncedBy: Schema.NullOr(UserId),
  createdAt: IsoDateTime,
});

/**
 * Opens the in-place trial of a pull request on a project environment.
 *
 * Emitted by `EnvironmentGitService` only after the merge went through and the
 * setup hook ran: an event that announced a trial the merge then refused would
 * leave the card advertising a pull request the worktree does not contain. A
 * conflict therefore produces no event at all — nothing happened.
 *
 * The snapshots travel in the command because they are what makes the trial
 * reversible, and because the copies were taken *before* the merge: recording
 * the trial without them would be recording a change with no way back.
 */
const EnvironmentTrialStartCommand = Schema.Struct({
  type: Schema.Literal("environment.trial.start"),
  commandId: CommandId,
  environmentId: EnvironmentId,
  prNumber: PositiveInt,
  headSha: TrimmedNonEmptyString,
  /** The human it is being tried for. Null when auth is off. */
  startedBy: Schema.NullOr(UserId),
  /** The conversation that asked for it, which the card links to. */
  threadId: Schema.NullOr(ThreadId),
  snapshots: Schema.Array(EnvironmentTrialSnapshot),
  createdAt: IsoDateTime,
});

/**
 * Closes it: the environment is back on its own branch and its data has been
 * restored.
 *
 * Also emitted by a plain `reset-to-base`, which does everything a "back to
 * base" does except restore the data. Leaving the trial open there would strand
 * the card on "Probando PR #131" for a worktree that no longer holds it, and a
 * badge nobody can clear is worse than one that clears a moment early.
 */
const EnvironmentTrialEndCommand = Schema.Struct({
  type: Schema.Literal("environment.trial.end"),
  commandId: CommandId,
  environmentId: EnvironmentId,
  createdAt: IsoDateTime,
});

/**
 * Records that somebody changed what is *inside* an environment — its rows or
 * its configuration file — rather than the code it runs.
 *
 * Its own command because the question it answers is one no other event can:
 * several conversations share one environment at once, and "why does QA have a
 * FEATURE_PAY of 'off' this morning?" is answerable only if the write left the
 * thread that made it behind. There is no mutex on these two operations — they
 * take milliseconds — so the trail is the whole mechanism, not a supplement to
 * one.
 *
 * `kind` picks the event; the payload is the same on both sides, because the
 * card renders one line either way.
 */
const EnvironmentModificationRecordCommand = Schema.Struct({
  type: Schema.Literal("environment.modification.record"),
  commandId: CommandId,
  environmentId: EnvironmentId,
  modificationKind: ProjectEnvironmentModificationKind,
  /** Thread whose agent or user made the change. Null for a server sweep. */
  threadId: Schema.NullOr(ThreadId),
  /** The human it was done for. Null when auth is off. */
  actorId: Schema.NullOr(UserId),
  /** One short line for the card, e.g. "qa_env_sql: INSERT". Never a value. */
  summary: TrimmedNonEmptyString,
  createdAt: IsoDateTime,
});

const EnvironmentProvisionCompleteCommand = Schema.Struct({
  type: Schema.Literal("environment.provision.complete"),
  commandId: CommandId,
  environmentId: EnvironmentId,
  createdAt: IsoDateTime,
});

const EnvironmentProvisionFailCommand = Schema.Struct({
  type: Schema.Literal("environment.provision.fail"),
  commandId: CommandId,
  environmentId: EnvironmentId,
  reason: TrimmedNonEmptyString,
  createdAt: IsoDateTime,
});

/**
 * Retires an environment. The row stays as history; the reactor removes the
 * worktree and the sandbox database afterwards.
 *
 * This is also how a failed provision is retried: destroying it releases the
 * `(user, project)` slot and cleans up whatever the failed attempt left behind.
 */
const EnvironmentDestroyCommand = Schema.Struct({
  type: Schema.Literal("environment.destroy"),
  commandId: CommandId,
  environmentId: EnvironmentId,
  createdAt: IsoDateTime,
});

/**
 * Records where an environment's preview is listening.
 *
 * Mirrors `thread.dev-server.allocate`: the manager emits it once the process is
 * up, and persisting it is what keeps the hostname and port sticky, so a
 * bookmarked preview URL survives a server restart.
 */
const EnvironmentDevServerAllocateCommand = Schema.Struct({
  type: Schema.Literal("environment.dev-server.allocate"),
  commandId: CommandId,
  environmentId: EnvironmentId,
  hostname: TrimmedNonEmptyString,
  port: NonNegativeInt,
  frameworkId: Schema.NullOr(DevServerFrameworkId),
  commandOverride: Schema.NullOr(TrimmedNonEmptyString),
  createdAt: IsoDateTime,
});

/** The environment counterpart of `thread.dev-server.set-visibility`. */
const EnvironmentDevServerSetVisibilityCommand = Schema.Struct({
  type: Schema.Literal("environment.dev-server.set-visibility"),
  commandId: CommandId,
  environmentId: EnvironmentId,
  publicUntil: Schema.NullOr(IsoDateTime),
  createdAt: IsoDateTime,
});

/**
 * Releases the port so another workspace can claim it.
 *
 * The hostname is deliberately *kept*: it is the environment's public address,
 * and handing it to somebody else because its owner stopped the preview for an
 * afternoon would break every link that was ever shared.
 */
const EnvironmentDevServerReleaseCommand = Schema.Struct({
  type: Schema.Literal("environment.dev-server.release"),
  commandId: CommandId,
  environmentId: EnvironmentId,
  createdAt: IsoDateTime,
});

/**
 * Commands of the `pr-preview` aggregate.
 *
 * Internal, like the environment ones and for the same reason: creating a
 * preview builds a worktree, restores a database copy and starts a dev server,
 * so *whether* it may happen is decided by the tool's permission check and the
 * project's `maxAlive`, and what reaches the log is the record of work the
 * server already committed to.
 *
 * Split the same way provisioning is — a request, then one of three outcomes —
 * because the build takes minutes and a single terminal command would leave a
 * half-built worktree invisible to the system if the server died in between.
 * `pr-preview.request` records the identity, the branch, the path and the commit
 * it starts from *before* any I/O; `.ready`, `.conflict` and `.fail` close it.
 */
const PrPreviewRequestCommand = Schema.Struct({
  type: Schema.Literal("pr-preview.request"),
  commandId: CommandId,
  previewId: PrPreviewId,
  projectId: ProjectId,
  /** Must be a live, `ready` environment of kind `project`. Enforced here. */
  baseEnvironmentId: EnvironmentId,
  prNumber: PositiveInt,
  /**
   * The commit of the base environment's worktree this preview branches from,
   * resolved before the row exists.
   *
   * Carried rather than looked up later because the environment moves: an agent
   * syncing `qa` an hour after a preview was created must not retroactively
   * change what that preview says it was built on.
   */
  baseSha: TrimmedNonEmptyString,
  branch: TrimmedNonEmptyString,
  worktreePath: TrimmedNonEmptyString,
  /** The human it is being created for. Null when auth is off. */
  requestedBy: Schema.NullOr(UserId),
  createdAt: IsoDateTime,
});

/** The preview is built and serving. Everything resolved during the build. */
const PrPreviewReadyCommand = Schema.Struct({
  type: Schema.Literal("pr-preview.ready"),
  commandId: CommandId,
  previewId: PrPreviewId,
  headSha: TrimmedNonEmptyString,
  hostname: TrimmedNonEmptyString,
  port: NonNegativeInt,
  dataSource: TrimmedNonEmptyString,
  expiresAt: IsoDateTime,
  createdAt: IsoDateTime,
});

/**
 * The pull request does not merge onto the base commit.
 *
 * Its own command rather than `fail` with a reason: the merge was aborted and
 * the workspace removed, so there is nothing to clean up and nothing to retry —
 * only files for a human to look at, and a `refresh` that will succeed once the
 * author rebases. See `PrPreviewStatus`.
 */
const PrPreviewConflictCommand = Schema.Struct({
  type: Schema.Literal("pr-preview.conflict"),
  commandId: CommandId,
  previewId: PrPreviewId,
  headSha: TrimmedNonEmptyString,
  conflicts: Schema.Array(TrimmedNonEmptyString),
  createdAt: IsoDateTime,
});

const PrPreviewFailCommand = Schema.Struct({
  type: Schema.Literal("pr-preview.fail"),
  commandId: CommandId,
  previewId: PrPreviewId,
  reason: TrimmedNonEmptyString,
  createdAt: IsoDateTime,
});

/**
 * The pull request was re-fetched and rebuilt on the same worktree.
 *
 * The successful outcome of a refresh, and therefore the one command that can
 * take a row out of `conflict`: the same preview, the same hostname, a newer
 * head. A refresh that conflicts or breaks emits `.conflict` or `.fail`
 * instead, so there is no state in which the row claims a head it does not hold.
 */
const PrPreviewRefreshCommand = Schema.Struct({
  type: Schema.Literal("pr-preview.refresh"),
  commandId: CommandId,
  previewId: PrPreviewId,
  headSha: TrimmedNonEmptyString,
  dataSource: TrimmedNonEmptyString,
  createdAt: IsoDateTime,
});

/** Moves the expiry out. The new absolute time, never a duration. */
const PrPreviewExtendCommand = Schema.Struct({
  type: Schema.Literal("pr-preview.extend"),
  commandId: CommandId,
  previewId: PrPreviewId,
  expiresAt: IsoDateTime,
  createdAt: IsoDateTime,
});

/**
 * Retires a preview: its worktree, databases and dev server are gone by the
 * time this is recorded, and the row stays as history.
 *
 * Emitted by the tool, by the environment card's button and by the expiry
 * sweep. Recording it is what frees the pull request's slot, so asking for
 * PR 131 again builds a new one rather than being told it already exists.
 */
const PrPreviewDestroyCommand = Schema.Struct({
  type: Schema.Literal("pr-preview.destroy"),
  commandId: CommandId,
  previewId: PrPreviewId,
  createdAt: IsoDateTime,
});

const DispatchableClientOrchestrationCommand = Schema.Union([
  ProjectLocalActionTemplateSetCommand,
  ProjectCreateCommand,
  ProjectMetaUpdateCommand,
  ProjectDeleteCommand,
  ThreadCreateCommand,
  ThreadHandoffCreateCommand,
  ThreadForkCreateCommand,
  ThreadDeleteCommand,
  ThreadArchiveCommand,
  ThreadUnarchiveCommand,
  ThreadSettleCommand,
  ThreadSnoozeCommand,
  ThreadRecapSetCommand,
  ThreadMetaUpdateCommand,
  ThreadRuntimeModeSetCommand,
  ThreadInteractionModeSetCommand,
  ThreadTurnStartCommand,
  ThreadTurnInterruptCommand,
  ThreadApprovalRespondCommand,
  ThreadUserInputRespondCommand,
  ThreadCheckpointRevertCommand,
  ThreadSessionStopCommand,
  ThreadContextClearCommand,
  ThreadDevCommandSetCommand,
  ThreadPlaneIssueSetCommand,
  ThreadPullRequestSetCommand,
  ThreadAssignUserCommand,
  ThreadUnassignUserCommand,
]);
export type DispatchableClientOrchestrationCommand =
  typeof DispatchableClientOrchestrationCommand.Type;

export const ClientOrchestrationCommand = Schema.Union([
  ProjectLocalActionTemplateSetCommand,
  ProjectCreateCommand,
  ProjectMetaUpdateCommand,
  ProjectDeleteCommand,
  ThreadCreateCommand,
  ThreadHandoffCreateCommand,
  ThreadForkCreateCommand,
  ThreadDeleteCommand,
  ThreadArchiveCommand,
  ThreadUnarchiveCommand,
  ClientThreadSettleCommand,
  ClientThreadSnoozeCommand,
  ThreadMetaUpdateCommand,
  ThreadRuntimeModeSetCommand,
  ThreadInteractionModeSetCommand,
  ClientThreadTurnStartCommand,
  ThreadTurnInterruptCommand,
  ThreadApprovalRespondCommand,
  ThreadUserInputRespondCommand,
  ThreadCheckpointRevertCommand,
  ThreadSessionStopCommand,
  ThreadContextClearCommand,
  ThreadDevCommandSetCommand,
  ThreadPlaneIssueSetCommand,
  ThreadPullRequestSetCommand,
  ThreadAssignUserCommand,
  ThreadUnassignUserCommand,
]);
export type ClientOrchestrationCommand = typeof ClientOrchestrationCommand.Type;

const ThreadSessionSetCommand = Schema.Struct({
  type: Schema.Literal("thread.session.set"),
  commandId: CommandId,
  threadId: ThreadId,
  session: OrchestrationSession,
  createdAt: IsoDateTime,
});

const ThreadMessageAssistantDeltaCommand = Schema.Struct({
  type: Schema.Literal("thread.message.assistant.delta"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  delta: Schema.String,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

const ThreadMessageAssistantCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.message.assistant.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  messageId: MessageId,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

const ThreadProposedPlanUpsertCommand = Schema.Struct({
  type: Schema.Literal("thread.proposed-plan.upsert"),
  commandId: CommandId,
  threadId: ThreadId,
  proposedPlan: OrchestrationProposedPlan,
  createdAt: IsoDateTime,
});

const ThreadTurnDiffCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.turn.diff.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  turnId: TurnId,
  completedAt: IsoDateTime,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.optional(MessageId),
  checkpointTurnCount: NonNegativeInt,
  createdAt: IsoDateTime,
});

/**
 * Books a finished turn's token consumption, one entry per model.
 *
 * Attribution is resolved by the caller, not by the projector: `userId` and
 * `agentId` are looked up from the read side at ingestion time and frozen into
 * the event, so a rebuild of the projection replays the same numbers against
 * the same people even after a thread is reassigned or an agent is deleted.
 */
const ThreadTurnUsageRecordCommand = Schema.Struct({
  type: Schema.Literal("thread.turn-usage.record"),
  commandId: CommandId,
  threadId: ThreadId,
  turnId: TurnId,
  provider: ProviderKind,
  usage: Schema.Array(TurnModelUsage),
  /** Author of the message that triggered the turn; null when unattributable. */
  userId: Schema.optional(Schema.NullOr(UserId)),
  /** Managed agent the thread runs as, if any. */
  agentId: Schema.optional(Schema.NullOr(ManagedAgentId)),
  /**
   * What the tokens were spent on. Optional so the conversation path — by far
   * the busiest caller — keeps dispatching exactly what it did before, and so a
   * command replayed from an older log still decodes; both mean `"chat"`.
   */
  kind: Schema.optional(TurnUsageKind),
  createdAt: IsoDateTime,
});

const ThreadActivityAppendCommand = Schema.Struct({
  type: Schema.Literal("thread.activity.append"),
  commandId: CommandId,
  threadId: ThreadId,
  activity: OrchestrationThreadActivity,
  createdAt: IsoDateTime,
});

const ThreadRevertCompleteCommand = Schema.Struct({
  type: Schema.Literal("thread.revert.complete"),
  commandId: CommandId,
  threadId: ThreadId,
  turnCount: NonNegativeInt,
  createdAt: IsoDateTime,
});

const InternalOrchestrationCommand = Schema.Union([
  ThreadSessionSetCommand,
  ThreadMessageAssistantDeltaCommand,
  ThreadMessageAssistantCompleteCommand,
  ThreadProposedPlanUpsertCommand,
  ThreadTurnDiffCompleteCommand,
  ThreadTurnUsageRecordCommand,
  ThreadActivityAppendCommand,
  ThreadRevertCompleteCommand,
  ThreadDispatchQueuedTurnCommand,
  ThreadDevServerAllocateCommand,
  ThreadDevServerReleaseCommand,
  ThreadDevServerSetVisibilityCommand,
  EnvironmentProvisionCommand,
  EnvironmentProvisionCompleteCommand,
  EnvironmentProvisionFailCommand,
  EnvironmentDestroyCommand,
  EnvironmentSyncStateSetCommand,
  EnvironmentSyncRecordCommand,
  EnvironmentTrialStartCommand,
  EnvironmentTrialEndCommand,
  EnvironmentModificationRecordCommand,
  EnvironmentDevServerAllocateCommand,
  EnvironmentDevServerReleaseCommand,
  EnvironmentDevServerSetVisibilityCommand,
  PrPreviewRequestCommand,
  PrPreviewReadyCommand,
  PrPreviewConflictCommand,
  PrPreviewFailCommand,
  PrPreviewRefreshCommand,
  PrPreviewExtendCommand,
  PrPreviewDestroyCommand,
]);
export type InternalOrchestrationCommand = typeof InternalOrchestrationCommand.Type;

export const OrchestrationCommand = Schema.Union([
  DispatchableClientOrchestrationCommand,
  InternalOrchestrationCommand,
]);
export type OrchestrationCommand = typeof OrchestrationCommand.Type;

export const OrchestrationEventType = Schema.Literals([
  "project.created",
  "project.meta-updated",
  "project.base-branch-changed",
  "project.deleted",
  "thread.created",
  "thread.deleted",
  // Legacy desktop installs can still contain these rows in orchestration_events.
  "thread.archived",
  "thread.unarchived",
  "thread.user-assigned",
  "thread.user-unassigned",
  "thread.settled",
  "thread.snoozed",
  "thread.recap-updated",
  "thread.meta-updated",
  "thread.runtime-mode-set",
  "thread.interaction-mode-set",
  "thread.dev-command-set",
  "thread.plane-issue-linked",
  "thread.pull-request-linked",
  "thread.dev-server-allocated",
  "thread.dev-server-released",
  "thread.dev-server-visibility-set",
  "thread.message-sent",
  "thread.turn-queued",
  "thread.turn-start-requested",
  "thread.turn-interrupt-requested",
  "thread.approval-response-requested",
  "thread.user-input-response-requested",
  "thread.checkpoint-revert-requested",
  "thread.reverted",
  "thread.session-stop-requested",
  "thread.context-cleared",
  "thread.session-set",
  "thread.proposed-plan-upserted",
  "thread.turn-diff-completed",
  "thread.turn-usage-recorded",
  "thread.activity-appended",
  "environment.provision-requested",
  "environment.provisioned",
  "environment.provision-failed",
  "environment.destroyed",
  "environment.sync-state-changed",
  "environment.synced",
  "environment.trial-started",
  "environment.trial-ended",
  "environment.data-changed",
  "environment.config-changed",
  "environment.dev-server-allocated",
  "environment.dev-server-released",
  "environment.dev-server-visibility-set",
  "pr-preview.requested",
  "pr-preview.ready",
  "pr-preview.conflict",
  "pr-preview.failed",
  "pr-preview.refreshed",
  "pr-preview.extended",
  "pr-preview.destroyed",
]);
export type OrchestrationEventType = typeof OrchestrationEventType.Type;

export const OrchestrationAggregateKind = Schema.Literals([
  "project",
  "thread",
  "environment",
  "pr-preview",
]);
export type OrchestrationAggregateKind = typeof OrchestrationAggregateKind.Type;
export const OrchestrationActorKind = Schema.Literals(["client", "server", "provider"]);

export const ProjectCreatedPayload = Schema.Struct({
  projectId: ProjectId,
  title: TrimmedNonEmptyString,
  workspaceRoot: TrimmedNonEmptyString,
  defaultModelSelection: Schema.NullOr(ModelSelection),
  scripts: Schema.Array(ProjectScript),
  localActions: Schema.Array(ProjectLocalAction).pipe(Schema.withDecodingDefault(() => [])),
  linkedProjectIds: Schema.Array(ProjectId).pipe(Schema.withDecodingDefault(() => [])),
  agentIds: Schema.Array(ManagedAgentId).pipe(Schema.withDecodingDefault(() => [])),
  iconFileName: Schema.optional(Schema.NullOr(Schema.String)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  color: Schema.optional(Schema.NullOr(TagColor)).pipe(Schema.withDecodingDefault(() => null)),
  description: Schema.optional(Schema.NullOr(ProjectDescription)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ProjectMetaUpdatedPayload = Schema.Struct({
  projectId: ProjectId,
  title: Schema.optional(TrimmedNonEmptyString),
  workspaceRoot: Schema.optional(TrimmedNonEmptyString),
  defaultModelSelection: Schema.optional(Schema.NullOr(ModelSelection)),
  scripts: Schema.optional(Schema.Array(ProjectScript)),
  localActions: Schema.optional(Schema.Array(ProjectLocalAction)),
  linkedProjectIds: Schema.optional(Schema.Array(ProjectId)),
  agentIds: Schema.optional(Schema.Array(ManagedAgentId)),
  iconFileName: Schema.optional(Schema.NullOr(Schema.String)),
  color: Schema.optional(Schema.NullOr(TagColor)),
  description: Schema.optional(Schema.NullOr(ProjectDescription)),
  updatedAt: IsoDateTime,
});

/**
 * The project's base branch was set, changed or cleared.
 *
 * Its own event rather than a field of the update above: environments react to
 * it (a project environment cannot be created before it exists, and an existing
 * one that tracks a different branch has to say so), and a subscriber that had
 * to diff every metadata update to notice would eventually miss one.
 */
export const ProjectBaseBranchChangedPayload = Schema.Struct({
  projectId: ProjectId,
  baseBranch: Schema.NullOr(TrimmedNonEmptyString),
  updatedAt: IsoDateTime,
});

export const ProjectDeletedPayload = Schema.Struct({
  projectId: ProjectId,
  deletedAt: IsoDateTime,
});

export const EnvironmentDevServerAllocatedPayload = Schema.Struct({
  environmentId: EnvironmentId,
  hostname: TrimmedNonEmptyString,
  port: NonNegativeInt,
  frameworkId: Schema.NullOr(DevServerFrameworkId),
  commandOverride: Schema.NullOr(TrimmedNonEmptyString),
  updatedAt: IsoDateTime,
});

export const EnvironmentDevServerReleasedPayload = Schema.Struct({
  environmentId: EnvironmentId,
  updatedAt: IsoDateTime,
});

export const EnvironmentDevServerVisibilitySetPayload = Schema.Struct({
  environmentId: EnvironmentId,
  publicUntil: Schema.NullOr(IsoDateTime),
  updatedAt: IsoDateTime,
});

/**
 * The event that creates the environment row.
 *
 * `environment.provisioned` marks the setup hook finished; everything that
 * *describes* the environment travels here, because the row exists from the
 * moment provisioning is requested and a half-built worktree with no `kind` and
 * no name would be unattributable if the server died in between.
 */
export const EnvironmentProvisionRequestedPayload = Schema.Struct({
  environmentId: EnvironmentId,
  projectId: ProjectId,
  /** Null if and only if `kind` is `project`. */
  userId: Schema.NullOr(UserId),
  kind: ProjectEnvironmentKind.pipe(
    Schema.withDecodingDefault(() => DEFAULT_PROJECT_ENVIRONMENT_KIND),
  ),
  name: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  trackedBranch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  clonedFrom: Schema.optional(Schema.NullOr(EnvironmentCloneOrigin)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  branch: TrimmedNonEmptyString,
  worktreePath: TrimmedNonEmptyString,
  baseRef: TrimmedNonEmptyString,
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

/**
 * What the last `git fetch` + `rev-list` said about an environment.
 *
 * `unknown` with a `checkedAt` is a real answer, not a missing one: it means the
 * server looked and could not reach the remote, and the card shows the time so
 * nobody reads a stale count as current.
 */
export const EnvironmentSyncStateChangedPayload = Schema.Struct({
  environmentId: EnvironmentId,
  syncState: ProjectEnvironmentSyncState,
  behindBy: Schema.NullOr(NonNegativeInt),
  checkedAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

/** An environment was successfully moved onto its tracked branch. */
export const EnvironmentSyncedPayload = Schema.Struct({
  environmentId: EnvironmentId,
  syncedSha: TrimmedNonEmptyString,
  syncedAt: IsoDateTime,
  syncedBy: Schema.NullOr(UserId),
  updatedAt: IsoDateTime,
});

/**
 * A pull request is now merged into this environment, in place.
 *
 * Carries the whole trial rather than a reference to one, because the row it
 * writes *is* the trial: there is no other table, and F3 deliberately adds no
 * entity. The snapshots are the reversibility, so they are part of the fact.
 */
export const EnvironmentTrialStartedPayload = Schema.Struct({
  environmentId: EnvironmentId,
  prNumber: PositiveInt,
  headSha: TrimmedNonEmptyString,
  startedAt: IsoDateTime,
  startedBy: Schema.NullOr(UserId),
  threadId: Schema.NullOr(ThreadId),
  snapshots: Schema.Array(EnvironmentTrialSnapshot),
  updatedAt: IsoDateTime,
});

/**
 * The environment is back on its own branch.
 *
 * No payload beyond the identity and the time: what the trial *was* is in the
 * event that opened it, and repeating it here would be two records of one fact
 * that could disagree after a replay.
 */
export const EnvironmentTrialEndedPayload = Schema.Struct({
  environmentId: EnvironmentId,
  endedAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

/**
 * A change to an environment's data or configuration, and who made it.
 *
 * Shared by `environment.data-changed` and `environment.config-changed`: the
 * card shows one "last modified by … in thread …" line, and two payload shapes
 * for one line would only be two things to keep in step.
 *
 * `summary` is written by the server, never by the model, and never carries a
 * value: the whole point of `qa_env_config_set` is that the value it wrote does
 * not end up anywhere it could be read back.
 */
export const EnvironmentModifiedPayload = Schema.Struct({
  environmentId: EnvironmentId,
  modificationKind: ProjectEnvironmentModificationKind,
  threadId: Schema.NullOr(ThreadId),
  actorId: Schema.NullOr(UserId),
  summary: TrimmedNonEmptyString,
  modifiedAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const EnvironmentProvisionedPayload = Schema.Struct({
  environmentId: EnvironmentId,
  updatedAt: IsoDateTime,
});

export const EnvironmentProvisionFailedPayload = Schema.Struct({
  environmentId: EnvironmentId,
  reason: TrimmedNonEmptyString,
  updatedAt: IsoDateTime,
});

export const EnvironmentDestroyedPayload = Schema.Struct({
  environmentId: EnvironmentId,
  deletedAt: IsoDateTime,
});

/**
 * The event that creates the preview row.
 *
 * Everything that *describes* the preview travels here, for the reason
 * `environment.provision-requested` gives: the row exists from the moment the
 * build starts, and a worktree on disk that no row can attribute to a pull
 * request is a workspace nothing will ever clean up.
 */
export const PrPreviewRequestedPayload = Schema.Struct({
  previewId: PrPreviewId,
  projectId: ProjectId,
  baseEnvironmentId: EnvironmentId,
  prNumber: PositiveInt,
  baseSha: TrimmedNonEmptyString,
  branch: TrimmedNonEmptyString,
  worktreePath: TrimmedNonEmptyString,
  requestedBy: Schema.NullOr(UserId),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

/**
 * The preview is serving.
 *
 * `expiresAt` is part of *this* fact rather than of a later one: a preview that
 * became ready without an expiry would be a preview the sweep never destroys,
 * and the whole reason the sweep exists is that nobody remembers to.
 */
export const PrPreviewReadyPayload = Schema.Struct({
  previewId: PrPreviewId,
  headSha: TrimmedNonEmptyString,
  hostname: TrimmedNonEmptyString,
  port: NonNegativeInt,
  dataSource: TrimmedNonEmptyString,
  expiresAt: IsoDateTime,
  readyAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

/** The merge was refused and aborted; these are the files that clashed. */
export const PrPreviewConflictPayload = Schema.Struct({
  previewId: PrPreviewId,
  headSha: TrimmedNonEmptyString,
  conflicts: Schema.Array(TrimmedNonEmptyString),
  updatedAt: IsoDateTime,
});

export const PrPreviewFailedPayload = Schema.Struct({
  previewId: PrPreviewId,
  reason: TrimmedNonEmptyString,
  updatedAt: IsoDateTime,
});

/**
 * A newer head of the same pull request is now built on the same resources.
 *
 * No hostname or port: a refresh deliberately keeps both, so that the URL
 * somebody pasted in the pull request keeps working across every push the
 * author makes.
 */
export const PrPreviewRefreshedPayload = Schema.Struct({
  previewId: PrPreviewId,
  headSha: TrimmedNonEmptyString,
  dataSource: TrimmedNonEmptyString,
  refreshedAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const PrPreviewExtendedPayload = Schema.Struct({
  previewId: PrPreviewId,
  expiresAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const PrPreviewDestroyedPayload = Schema.Struct({
  previewId: PrPreviewId,
  destroyedAt: IsoDateTime,
});

export const ThreadCreatedPayload = Schema.Struct({
  threadId: ThreadId,
  projectId: ProjectId,
  kind: Schema.optional(Schema.Literals(["standard", "initiative"] as const)).pipe(
    Schema.withDecodingDefault(() => "standard"),
  ),
  title: TrimmedNonEmptyString,
  modelSelection: ModelSelection,
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(() => DEFAULT_RUNTIME_MODE)),
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(() => DEFAULT_PROVIDER_INTERACTION_MODE),
  ),
  envMode: Schema.optional(ThreadEnvironmentMode).pipe(Schema.withDecodingDefault(() => "local")),
  branch: Schema.NullOr(TrimmedNonEmptyString),
  worktreePath: Schema.NullOr(TrimmedNonEmptyString),
  /**
   * Environment whose workspace this thread borrows, when it has one.
   *
   * Null for the ordinary case: the thread owns its worktree and loses it when
   * archived. Non-null makes the thread a *guest* — its cwd, its preview and
   * its sandbox database belong to the environment, several threads share them
   * at once, and archiving the thread destroys none of it.
   */
  environmentId: Schema.optional(Schema.NullOr(EnvironmentId)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  associatedWorktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  associatedWorktreeBranch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  associatedWorktreeRef: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  associatedAgentId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  parentThreadId: Schema.optional(Schema.NullOr(ThreadId)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  subagentAgentId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  subagentNickname: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  subagentRole: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  forkSourceThreadId: Schema.optional(Schema.NullOr(ThreadId)).pipe(
    Schema.withDecodingDefault(() => null),
  ),
  handoff: Schema.NullOr(ThreadHandoff).pipe(Schema.withDecodingDefault(() => null)),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ThreadDeletedPayload = Schema.Struct({
  threadId: ThreadId,
  deletedAt: IsoDateTime,
});

export const ThreadArchivedPayload = Schema.Struct({
  threadId: ThreadId,
  // Required for new events, optional for legacy events
  archivedAt: Schema.optional(IsoDateTime),
  updatedAt: Schema.optional(IsoDateTime),
});

export const ThreadUnarchivedPayload = Schema.Struct({
  threadId: ThreadId,
  // Legacy field - kept for backward compatibility with old events
  unarchivedAt: Schema.optional(IsoDateTime),
  // Required for new events
  updatedAt: Schema.optional(IsoDateTime),
});

/**
 * A user was granted access to a thread.
 *
 * Who granted it is not repeated here: the dispatcher stamps the authenticated
 * actor on `metadata.actorId`, which is the only non-spoofable source, and the
 * projectors read `assigned_by` from there — the same way thread ownership is
 * recorded.
 */
export const ThreadUserAssignedPayload = Schema.Struct({
  threadId: ThreadId,
  userId: UserId,
  assignedAt: IsoDateTime,
});

export const ThreadUserUnassignedPayload = Schema.Struct({
  threadId: ThreadId,
  userId: UserId,
  unassignedAt: IsoDateTime,
});

/**
 * One person marked a thread done, or undid it.
 *
 * A single event per aspect with a nullable instant, rather than a settled and
 * an unsettled event. The pair reads better in a log, but every consumer would
 * then have to know that the two are about the same column, and a replay that
 * dropped one of them would leave the projection stuck in whichever state came
 * first. `settledAt: null` says exactly what the row becomes.
 */
export const ThreadSettledPayload = Schema.Struct({
  threadId: ThreadId,
  userId: UserId,
  settledAt: Schema.NullOr(IsoDateTime),
  updatedAt: IsoDateTime,
});

/**
 * A thread's recap was rewritten.
 *
 * Unlike `thread.settled` and `thread.snoozed`, which carry a `userId` because
 * they are about one person's inbox, this is a property of the thread and goes
 * to everyone who can see it. It replaces the previous text outright: a recap
 * describes the present, so there is nothing to merge with what it supersedes.
 */
export const ThreadRecapUpdatedPayload = Schema.Struct({
  threadId: ThreadId,
  text: TrimmedNonEmptyString,
  generatedAt: IsoDateTime,
  sourceTurnId: Schema.NullOr(TurnId),
  model: Schema.NullOr(TrimmedNonEmptyString),
  updatedAt: IsoDateTime,
});

/**
 * One person snoozed a thread, or their snooze ended.
 *
 * Waking is `snoozedUntil: null`, whether the person cancelled it by hand or
 * the sweep found the deadline passed — from the projection's side those are
 * the same fact, and giving the sweep its own event type would mean two ways to
 * arrive at one state, which the reducers would have to keep in agreement.
 */
export const ThreadSnoozedPayload = Schema.Struct({
  threadId: ThreadId,
  userId: UserId,
  snoozedUntil: Schema.NullOr(IsoDateTime),
  /**
   * Present, and equal to the moment it happened, only when the sweep ended the
   * snooze. Optional rather than nullable so an event from before this existed
   * decodes unchanged.
   */
  wokenAt: Schema.optional(IsoDateTime),
  updatedAt: IsoDateTime,
});

export const ThreadMetaUpdatedPayload = Schema.Struct({
  threadId: ThreadId,
  title: Schema.optional(TrimmedNonEmptyString),
  modelSelection: Schema.optional(ModelSelection),
  envMode: Schema.optional(ThreadEnvironmentMode),
  branch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  worktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  associatedWorktreePath: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  associatedWorktreeBranch: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  associatedWorktreeRef: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  parentThreadId: Schema.optional(Schema.NullOr(ThreadId)),
  subagentAgentId: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  subagentNickname: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  subagentRole: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  handoff: Schema.optional(Schema.NullOr(ThreadHandoff)),
  updatedAt: IsoDateTime,
});

export const ThreadRuntimeModeSetPayload = Schema.Struct({
  threadId: ThreadId,
  runtimeMode: RuntimeMode,
  updatedAt: IsoDateTime,
});

export const ThreadDevCommandSetPayload = Schema.Struct({
  threadId: ThreadId,
  devCommand: Schema.NullOr(TrimmedNonEmptyString),
  updatedAt: IsoDateTime,
});

/**
 * The thread now works this issue — or, with both fields null, no issue at all.
 *
 * Carries the readable ref and the UUID together so nothing downstream has to
 * call Plane just to render the link.
 */
export const ThreadPlaneIssueLinkedPayload = Schema.Struct({
  threadId: ThreadId,
  planeIssueRef: Schema.NullOr(PlaneIssueRef),
  planeIssueId: Schema.NullOr(TrimmedNonEmptyString),
  updatedAt: IsoDateTime,
});

/**
 * The thread now works this pull request — or, with `null`, none.
 *
 * Carries the address and nothing else, for the reason
 * `OrchestrationThread.linkedPullRequest` gives: a title or a state folded in
 * here would be frozen at the instant of the link and wrong from then on.
 */
export const ThreadPullRequestLinkedPayload = Schema.Struct({
  threadId: ThreadId,
  pullRequest: Schema.NullOr(PullRequestRef),
  updatedAt: IsoDateTime,
});

export const ThreadDevServerAllocatedPayload = Schema.Struct({
  threadId: ThreadId,
  hostname: TrimmedNonEmptyString,
  port: NonNegativeInt,
  frameworkId: Schema.NullOr(DevServerFrameworkId),
  allocatedAt: IsoDateTime,
});

export const ThreadDevServerReleasedPayload = Schema.Struct({
  threadId: ThreadId,
  releasedAt: IsoDateTime,
});

export const ThreadDevServerVisibilitySetPayload = Schema.Struct({
  threadId: ThreadId,
  publicUntil: Schema.NullOr(IsoDateTime),
  updatedAt: IsoDateTime,
});

export const ThreadInteractionModeSetPayload = Schema.Struct({
  threadId: ThreadId,
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(() => DEFAULT_PROVIDER_INTERACTION_MODE),
  ),
  updatedAt: IsoDateTime,
});

export const ThreadMessageSentPayload = Schema.Struct({
  threadId: ThreadId,
  messageId: MessageId,
  role: OrchestrationMessageRole,
  text: Schema.String,
  attachments: Schema.optional(Schema.Array(ChatAttachment)),
  skills: Schema.optional(Schema.Array(ProviderSkillReference)),
  mentions: Schema.optional(Schema.Array(ProviderMentionReference)),
  turnId: Schema.NullOr(TurnId),
  streaming: Schema.Boolean,
  source: OrchestrationMessageSource.pipe(Schema.withDecodingDefault(() => "native")),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});

export const ThreadTurnStartRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  messageId: MessageId,
  modelSelection: Schema.optional(ModelSelection),
  providerOptions: Schema.optional(ProviderStartOptions),
  reviewTarget: Schema.optional(ProviderReviewTarget),
  assistantDeliveryMode: Schema.optional(AssistantDeliveryMode),
  dispatchMode: TurnDispatchMode.pipe(Schema.withDecodingDefault(() => DEFAULT_TURN_DISPATCH_MODE)),
  runtimeMode: RuntimeMode.pipe(Schema.withDecodingDefault(() => DEFAULT_RUNTIME_MODE)),
  interactionMode: ProviderInteractionMode.pipe(
    Schema.withDecodingDefault(() => DEFAULT_PROVIDER_INTERACTION_MODE),
  ),
  sourceProposedPlan: Schema.optional(SourceProposedPlanReference),
  createdAt: IsoDateTime,
});

export const ThreadTurnQueuedPayload = ThreadTurnStartRequestedPayload;

export const ThreadTurnInterruptRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  turnId: Schema.optional(TurnId),
  createdAt: IsoDateTime,
});

export const ThreadApprovalResponseRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  decision: ProviderApprovalDecision,
  createdAt: IsoDateTime,
});

const ThreadUserInputResponseRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  requestId: ApprovalRequestId,
  answers: ProviderUserInputAnswers,
  createdAt: IsoDateTime,
});

export const ThreadCheckpointRevertRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  turnCount: NonNegativeInt,
  createdAt: IsoDateTime,
});

export const ThreadRevertedPayload = Schema.Struct({
  threadId: ThreadId,
  turnCount: NonNegativeInt,
});

export const ThreadSessionStopRequestedPayload = Schema.Struct({
  threadId: ThreadId,
  createdAt: IsoDateTime,
});

/**
 * Nothing is deleted here: the payload only records where the conversation
 * view now starts (`clearedAt`) and carries the marker row the timeline shows
 * in place of the hidden history. The marker travels in the payload rather
 * than being rebuilt by each projector so the in-memory read model and the
 * persisted projection can never drift on its id, wording or ordering.
 */
export const ThreadContextClearedPayload = Schema.Struct({
  threadId: ThreadId,
  clearedAt: IsoDateTime,
  activity: OrchestrationThreadActivity,
});

export const ThreadSessionSetPayload = Schema.Struct({
  threadId: ThreadId,
  session: OrchestrationSession,
});

export const ThreadProposedPlanUpsertedPayload = Schema.Struct({
  threadId: ThreadId,
  proposedPlan: OrchestrationProposedPlan,
});

export const ThreadTurnDiffCompletedPayload = Schema.Struct({
  threadId: ThreadId,
  turnId: TurnId,
  checkpointTurnCount: NonNegativeInt,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.NullOr(MessageId),
  completedAt: IsoDateTime,
});

/**
 * A turn's token consumption, already attributed. `usage` holds one entry per
 * model, and `createdAt` is what the reporting range filters on — the moment
 * the turn finished, not the moment the projector got to it.
 */
export const ThreadTurnUsageRecordedPayload = Schema.Struct({
  threadId: ThreadId,
  turnId: TurnId,
  provider: ProviderKind,
  usage: Schema.Array(TurnModelUsage),
  userId: Schema.NullOr(UserId),
  agentId: Schema.NullOr(ManagedAgentId),
  /**
   * Optional here rather than defaulted, unlike `userId`/`agentId` above, because
   * events already in the log were written without it. The projector reads an
   * absent value as `"chat"`, which is what those rows are.
   */
  kind: Schema.optional(TurnUsageKind),
  createdAt: IsoDateTime,
});

export const ThreadActivityAppendedPayload = Schema.Struct({
  threadId: ThreadId,
  activity: OrchestrationThreadActivity,
});

export const OrchestrationEventMetadata = Schema.Struct({
  providerTurnId: Schema.optional(TrimmedNonEmptyString),
  providerItemId: Schema.optional(ProviderItemId),
  adapterKey: Schema.optional(TrimmedNonEmptyString),
  requestId: Schema.optional(ApprovalRequestId),
  ingestedAt: Schema.optional(IsoDateTime),
  /**
   * User the command is attributed to.
   *
   * Set by the dispatcher from the authenticated session, not by the caller of
   * a command, so it cannot be spoofed by whoever builds the payload. Absent
   * for events the server or a provider raises on its own.
   */
  actorId: Schema.optional(UserId),
});
export type OrchestrationEventMetadata = typeof OrchestrationEventMetadata.Type;

const EventBaseFields = {
  sequence: NonNegativeInt,
  eventId: EventId,
  aggregateKind: OrchestrationAggregateKind,
  aggregateId: Schema.Union([ProjectId, ThreadId, EnvironmentId, PrPreviewId]),
  occurredAt: IsoDateTime,
  commandId: Schema.NullOr(CommandId),
  causationEventId: Schema.NullOr(EventId),
  correlationId: Schema.NullOr(CommandId),
  metadata: OrchestrationEventMetadata,
} as const;

export const OrchestrationEvent = Schema.Union([
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("project.created"),
    payload: ProjectCreatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("project.meta-updated"),
    payload: ProjectMetaUpdatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("project.base-branch-changed"),
    payload: ProjectBaseBranchChangedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("project.deleted"),
    payload: ProjectDeletedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.created"),
    payload: ThreadCreatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("environment.provision-requested"),
    payload: EnvironmentProvisionRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("environment.provisioned"),
    payload: EnvironmentProvisionedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("environment.provision-failed"),
    payload: EnvironmentProvisionFailedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("environment.destroyed"),
    payload: EnvironmentDestroyedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("environment.sync-state-changed"),
    payload: EnvironmentSyncStateChangedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("environment.synced"),
    payload: EnvironmentSyncedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("environment.trial-started"),
    payload: EnvironmentTrialStartedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("environment.trial-ended"),
    payload: EnvironmentTrialEndedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("environment.data-changed"),
    payload: EnvironmentModifiedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("environment.config-changed"),
    payload: EnvironmentModifiedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("environment.dev-server-allocated"),
    payload: EnvironmentDevServerAllocatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("environment.dev-server-released"),
    payload: EnvironmentDevServerReleasedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("environment.dev-server-visibility-set"),
    payload: EnvironmentDevServerVisibilitySetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("pr-preview.requested"),
    payload: PrPreviewRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("pr-preview.ready"),
    payload: PrPreviewReadyPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("pr-preview.conflict"),
    payload: PrPreviewConflictPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("pr-preview.failed"),
    payload: PrPreviewFailedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("pr-preview.refreshed"),
    payload: PrPreviewRefreshedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("pr-preview.extended"),
    payload: PrPreviewExtendedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("pr-preview.destroyed"),
    payload: PrPreviewDestroyedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.deleted"),
    payload: ThreadDeletedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.archived"),
    payload: ThreadArchivedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.unarchived"),
    payload: ThreadUnarchivedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.settled"),
    payload: ThreadSettledPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.snoozed"),
    payload: ThreadSnoozedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.recap-updated"),
    payload: ThreadRecapUpdatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.user-assigned"),
    payload: ThreadUserAssignedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.user-unassigned"),
    payload: ThreadUserUnassignedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.meta-updated"),
    payload: ThreadMetaUpdatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.runtime-mode-set"),
    payload: ThreadRuntimeModeSetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.interaction-mode-set"),
    payload: ThreadInteractionModeSetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.plane-issue-linked"),
    payload: ThreadPlaneIssueLinkedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.pull-request-linked"),
    payload: ThreadPullRequestLinkedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.dev-command-set"),
    payload: ThreadDevCommandSetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.dev-server-allocated"),
    payload: ThreadDevServerAllocatedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.dev-server-released"),
    payload: ThreadDevServerReleasedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.dev-server-visibility-set"),
    payload: ThreadDevServerVisibilitySetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.message-sent"),
    payload: ThreadMessageSentPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.turn-queued"),
    payload: ThreadTurnQueuedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.turn-start-requested"),
    payload: ThreadTurnStartRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.turn-interrupt-requested"),
    payload: ThreadTurnInterruptRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.approval-response-requested"),
    payload: ThreadApprovalResponseRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.user-input-response-requested"),
    payload: ThreadUserInputResponseRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.checkpoint-revert-requested"),
    payload: ThreadCheckpointRevertRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.reverted"),
    payload: ThreadRevertedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.session-stop-requested"),
    payload: ThreadSessionStopRequestedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.context-cleared"),
    payload: ThreadContextClearedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.session-set"),
    payload: ThreadSessionSetPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.proposed-plan-upserted"),
    payload: ThreadProposedPlanUpsertedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.turn-diff-completed"),
    payload: ThreadTurnDiffCompletedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.turn-usage-recorded"),
    payload: ThreadTurnUsageRecordedPayload,
  }),
  Schema.Struct({
    ...EventBaseFields,
    type: Schema.Literal("thread.activity-appended"),
    payload: ThreadActivityAppendedPayload,
  }),
]);
export type OrchestrationEvent = typeof OrchestrationEvent.Type;

/**
 * How much of a thread's history a detail snapshot carries.
 *
 * Opening a thread used to ship every message and activity it ever had, which
 * is unbounded work on the server and an unbounded synchronous decode on the
 * client. The snapshot now carries only the newest slice; the rest is fetched
 * on demand with `orchestration.loadOlderThreadHistory`.
 *
 * Two budgets rather than one because the two streams grow at very different
 * rates: a single turn writes one assistant message but can write dozens of
 * tool activities, so a shared budget would let activity spam evict the
 * conversation itself.
 */
export const THREAD_HISTORY_MESSAGE_PAGE_SIZE = 100;
export const THREAD_HISTORY_ACTIVITY_PAGE_SIZE = 400;

/**
 * Position of one row inside its stream's ordering.
 *
 * `createdAt` alone is not a key — rows written in the same millisecond are
 * common — so `id` breaks the tie and the pair is compared row-wise. This is
 * exactly the `(created_at, id)` ordering the server pages by, which is what
 * makes "everything strictly before this cursor" return no duplicates and
 * leave no holes.
 */
export const OrchestrationHistoryCursor = Schema.Struct({
  createdAt: IsoDateTime,
  id: TrimmedNonEmptyString,
});
export type OrchestrationHistoryCursor = typeof OrchestrationHistoryCursor.Type;

/**
 * One cursor per paged stream, not one cursor for the thread.
 *
 * Messages and activities have independent budgets, so their pages end at
 * different instants. A single shared cursor would have to pick one of the two
 * boundaries: the older one re-sends rows the client already has, the newer one
 * skips rows that were never sent at all. Keeping them apart makes each stream
 * exact.
 *
 * `null` for a stream means the client holds nothing from it, which only
 * happens when that stream was already empty at the history floor — so there is
 * nothing older to ask for either.
 */
export const OrchestrationThreadHistoryCursor = Schema.Struct({
  messages: Schema.NullOr(OrchestrationHistoryCursor),
  activities: Schema.NullOr(OrchestrationHistoryCursor),
});
export type OrchestrationThreadHistoryCursor = typeof OrchestrationThreadHistoryCursor.Type;

export const OrchestrationThreadDetailSnapshot = Schema.Struct({
  snapshotSequence: NonNegativeInt,
  thread: OrchestrationThread,
  /**
   * Whether any row older than this snapshot's window is still reachable.
   *
   * False both when the thread is short and when the window already reaches the
   * history floor (the `/clear` or compaction cut-off), because the floor is
   * absolute: nothing below it is ever served again.
   *
   * Optional so an older client ignores it and an older server simply omits it,
   * matching how `deliverySequence` degrades.
   */
  hasMoreHistory: Schema.optional(Schema.Boolean),
  /** Oldest row this snapshot carries, per stream. Feed it back verbatim. */
  oldestCursor: Schema.optional(OrchestrationThreadHistoryCursor),
});
export type OrchestrationThreadDetailSnapshot = typeof OrchestrationThreadDetailSnapshot.Type;

/**
 * Per-subscription delivery counter for the thread stream.
 *
 * The event `sequence` is the global event-log id, so consecutive events of one
 * thread are never contiguous — a client cannot tell a lost push apart from
 * events that belonged to other threads. This counter is stamped per (client,
 * thread) subscription, so a jump in it is proof that a push was lost and the
 * client must re-snapshot. Optional so an older server (or a snapshot replayed
 * from elsewhere) simply disables the check instead of faking gaps.
 */
export const OrchestrationThreadStreamItem = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("snapshot"),
    snapshot: OrchestrationThreadDetailSnapshot,
    deliverySequence: Schema.optional(NonNegativeInt),
  }),
  Schema.Struct({
    kind: Schema.Literal("event"),
    event: OrchestrationEvent,
    deliverySequence: Schema.optional(NonNegativeInt),
  }),
]);
export type OrchestrationThreadStreamItem = typeof OrchestrationThreadStreamItem.Type;

export const OrchestrationCommandReceiptStatus = Schema.Literals(["accepted", "rejected"]);
export type OrchestrationCommandReceiptStatus = typeof OrchestrationCommandReceiptStatus.Type;

export const TurnCountRange = Schema.Struct({
  fromTurnCount: NonNegativeInt,
  toTurnCount: NonNegativeInt,
}).check(
  Schema.makeFilter(
    (input) =>
      input.fromTurnCount <= input.toTurnCount ||
      new SchemaIssue.InvalidValue(Option.some(input.fromTurnCount), {
        message: "fromTurnCount must be less than or equal to toTurnCount",
      }),
    { identifier: "OrchestrationTurnDiffRange" },
  ),
);

export const ThreadTurnDiff = TurnCountRange.mapFields(
  Struct.assign({
    threadId: ThreadId,
    diff: Schema.String,
  }),
  { unsafePreserveChecks: true },
);

export const ProviderSessionRuntimeStatus = Schema.Literals([
  "starting",
  "running",
  "stopped",
  "error",
]);
export type ProviderSessionRuntimeStatus = typeof ProviderSessionRuntimeStatus.Type;

const ProjectionThreadTurnStatus = Schema.Literals([
  "running",
  "completed",
  "interrupted",
  "error",
]);
export type ProjectionThreadTurnStatus = typeof ProjectionThreadTurnStatus.Type;

const ProjectionCheckpointRow = Schema.Struct({
  threadId: ThreadId,
  turnId: TurnId,
  checkpointTurnCount: NonNegativeInt,
  checkpointRef: CheckpointRef,
  status: OrchestrationCheckpointStatus,
  files: Schema.Array(OrchestrationCheckpointFile),
  assistantMessageId: Schema.NullOr(MessageId),
  completedAt: IsoDateTime,
});
export type ProjectionCheckpointRow = typeof ProjectionCheckpointRow.Type;

export const ProjectionPendingApprovalStatus = Schema.Literals(["pending", "resolved"]);
export type ProjectionPendingApprovalStatus = typeof ProjectionPendingApprovalStatus.Type;

export const ProjectionPendingApprovalDecision = Schema.NullOr(ProviderApprovalDecision);
export type ProjectionPendingApprovalDecision = typeof ProjectionPendingApprovalDecision.Type;

export const DispatchResult = Schema.Struct({
  sequence: NonNegativeInt,
});
export type DispatchResult = typeof DispatchResult.Type;

export const OrchestrationGetSnapshotInput = Schema.Struct({});
export type OrchestrationGetSnapshotInput = typeof OrchestrationGetSnapshotInput.Type;
const OrchestrationGetSnapshotResult = OrchestrationReadModel;
export type OrchestrationGetSnapshotResult = typeof OrchestrationGetSnapshotResult.Type;

export const OrchestrationRepairStateInput = Schema.Struct({});
export type OrchestrationRepairStateInput = typeof OrchestrationRepairStateInput.Type;
const OrchestrationRepairStateResult = OrchestrationReadModel;
export type OrchestrationRepairStateResult = typeof OrchestrationRepairStateResult.Type;

export const OrchestrationGetTurnDiffInput = TurnCountRange.mapFields(
  Struct.assign({ threadId: ThreadId }),
  { unsafePreserveChecks: true },
);
export type OrchestrationGetTurnDiffInput = typeof OrchestrationGetTurnDiffInput.Type;

export const OrchestrationGetTurnDiffResult = ThreadTurnDiff;
export type OrchestrationGetTurnDiffResult = typeof OrchestrationGetTurnDiffResult.Type;

export const OrchestrationGetFullThreadDiffInput = Schema.Struct({
  threadId: ThreadId,
  toTurnCount: NonNegativeInt,
});
export type OrchestrationGetFullThreadDiffInput = typeof OrchestrationGetFullThreadDiffInput.Type;

export const OrchestrationGetFullThreadDiffResult = ThreadTurnDiff;
export type OrchestrationGetFullThreadDiffResult = typeof OrchestrationGetFullThreadDiffResult.Type;

export const OrchestrationReplayEventsInput = Schema.Struct({
  fromSequenceExclusive: NonNegativeInt,
});
export type OrchestrationReplayEventsInput = typeof OrchestrationReplayEventsInput.Type;

const OrchestrationReplayEventsResult = Schema.Array(OrchestrationEvent);
export type OrchestrationReplayEventsResult = typeof OrchestrationReplayEventsResult.Type;

export const OrchestrationSubscribeShellInput = Schema.Struct({});
export type OrchestrationSubscribeShellInput = typeof OrchestrationSubscribeShellInput.Type;

export const OrchestrationUnsubscribeShellInput = Schema.Struct({});
export type OrchestrationUnsubscribeShellInput = typeof OrchestrationUnsubscribeShellInput.Type;

export const OrchestrationSubscribeThreadInput = Schema.Struct({
  threadId: ThreadId,
});
export type OrchestrationSubscribeThreadInput = typeof OrchestrationSubscribeThreadInput.Type;

export const OrchestrationUnsubscribeThreadInput = Schema.Struct({
  threadId: ThreadId,
});
export type OrchestrationUnsubscribeThreadInput = typeof OrchestrationUnsubscribeThreadInput.Type;

/**
 * How far behind a thread may fall before replay stops being worth it.
 *
 * Past this many pending events, refetching the (now bounded) detail snapshot
 * costs less than shipping and folding the individual events — and the events
 * would mostly be rebuilding state the snapshot already carries. The number is
 * deliberately well above a normal reconnect blip: a single busy turn writes
 * tens of activity events, not hundreds.
 */
export const THREAD_EVENT_REPLAY_LIMIT = 500;

export const OrchestrationReplayThreadEventsInput = Schema.Struct({
  threadId: ThreadId,
  /** The newest event sequence the client has already applied to this thread. */
  fromSequenceExclusive: NonNegativeInt,
});
export type OrchestrationReplayThreadEventsInput = typeof OrchestrationReplayThreadEventsInput.Type;

/**
 * The events one thread produced after a cursor, or an order to re-snapshot.
 *
 * `snapshotRequired` is the escape hatch for the two cases replay cannot serve:
 * more than `THREAD_EVENT_REPLAY_LIMIT` events are pending, or the thread is
 * gone. `events` is empty whenever it is set.
 *
 * `deliverySequence` is the per-subscription counter the server burned on this
 * reply, exactly as a snapshot burns one. Adopting it lets the client keep
 * detecting lost pushes immediately after a replay instead of having to treat
 * the next push as an unverifiable baseline.
 */
export const OrchestrationReplayThreadEventsResult = Schema.Struct({
  snapshotRequired: Schema.Boolean,
  events: Schema.Array(OrchestrationEvent),
  deliverySequence: Schema.optional(NonNegativeInt),
});
export type OrchestrationReplayThreadEventsResult =
  typeof OrchestrationReplayThreadEventsResult.Type;

export const OrchestrationLoadOlderThreadHistoryInput = Schema.Struct({
  threadId: ThreadId,
  /** The `oldestCursor` the client currently holds, echoed back unchanged. */
  beforeCursor: OrchestrationThreadHistoryCursor,
});
export type OrchestrationLoadOlderThreadHistoryInput =
  typeof OrchestrationLoadOlderThreadHistoryInput.Type;

/**
 * A strictly-older slice of one thread's history.
 *
 * Carries no proposed plans: those are never paged, so the detail snapshot
 * already holds every plan above the history floor and repeating them here
 * could only produce duplicates.
 *
 * Rows arrive in the same order the snapshot uses, so the client prepends the
 * arrays rather than re-sorting them.
 */
export const OrchestrationLoadOlderThreadHistoryResult = Schema.Struct({
  threadId: ThreadId,
  messages: Schema.Array(OrchestrationMessage),
  activities: Schema.Array(OrchestrationThreadActivity),
  hasMoreHistory: Schema.Boolean,
  /**
   * Oldest row now held, per stream — the snapshot's cursor advanced by this
   * page. A stream that returned nothing keeps the cursor it came in with, so
   * a stream that is exhausted does not reset the other one.
   */
  oldestCursor: OrchestrationThreadHistoryCursor,
});
export type OrchestrationLoadOlderThreadHistoryResult =
  typeof OrchestrationLoadOlderThreadHistoryResult.Type;

// ── Project repo credentials (Fase 1 — clonado con token GitHub) ──────

export const SetProjectRepoCredentialsInput = Schema.Struct({
  projectId: ProjectId,
  repositoryUrl: TrimmedNonEmptyString,
  accessToken: TrimmedNonEmptyString,
});
export type SetProjectRepoCredentialsInput = typeof SetProjectRepoCredentialsInput.Type;

export const SetProjectRepoCredentialsResult = Schema.Struct({
  status: ProjectRepoCredentialStatus,
  error: Schema.NullOr(Schema.String),
});
export type SetProjectRepoCredentialsResult = typeof SetProjectRepoCredentialsResult.Type;

export const ValidateProjectRepoCredentialsInput = Schema.Struct({
  projectId: ProjectId,
});
export type ValidateProjectRepoCredentialsInput = typeof ValidateProjectRepoCredentialsInput.Type;

export const ValidateProjectRepoCredentialsResult = SetProjectRepoCredentialsResult;
export type ValidateProjectRepoCredentialsResult = typeof ValidateProjectRepoCredentialsResult.Type;

export const ClearProjectRepoCredentialsInput = Schema.Struct({
  projectId: ProjectId,
});
export type ClearProjectRepoCredentialsInput = typeof ClearProjectRepoCredentialsInput.Type;

// ── Create project from GitHub repository ─────────────────────────────────
// End-to-end flow: validate token → clone bare → persist credentials →
// dispatch `project.create` against the bare's path. The server owns the
// projectId so the response is the bridge between the UI's "add project"
// optimism and the server-side bare layout.

export const CreateProjectFromRepositoryInput = Schema.Struct({
  repositoryUrl: TrimmedNonEmptyString,
  accessToken: TrimmedNonEmptyString,
  /** Optional display title; defaults to the repo name. */
  title: Schema.optional(TrimmedNonEmptyString),
});
export type CreateProjectFromRepositoryInput = typeof CreateProjectFromRepositoryInput.Type;

export const CreateProjectFromRepositoryResult = Schema.Union([
  Schema.Struct({
    status: Schema.Literal("created"),
    projectId: ProjectId,
    // Bare mirror path. Not for user editing — only the source for worktrees.
    workspaceRoot: TrimmedNonEmptyString,
    defaultBranch: TrimmedNonEmptyString,
    // Initial thread + worktree created for the user who triggered the clone.
    // Multiple users on the same project work on different branches, so each
    // thread/worktree pair is isolated by design.
    threadId: ThreadId,
    threadBranch: TrimmedNonEmptyString,
    threadWorktreePath: TrimmedNonEmptyString,
  }),
  Schema.Struct({
    status: Schema.Literal("invalid"),
    error: Schema.String,
  }),
]);
export type CreateProjectFromRepositoryResult = typeof CreateProjectFromRepositoryResult.Type;

// ── Thread dev server (runtime + preview hostname forwarding) ───────────

export const StartThreadDevServerInput = Schema.Struct({
  threadId: ThreadId,
  commandOverride: Schema.optional(Schema.NullOr(TrimmedNonEmptyString)),
  /** Which detected framework to boot when the worktree matched several. */
  frameworkId: Schema.optional(Schema.NullOr(DevServerFrameworkId)),
});
export type StartThreadDevServerInput = typeof StartThreadDevServerInput.Type;

export const GetThreadDevServerInput = Schema.Struct({
  threadId: ThreadId,
});
export type GetThreadDevServerInput = typeof GetThreadDevServerInput.Type;

/**
 * Everything the dev server panel needs for a thread: current runtime status,
 * what we detected in the worktree, and the command that would actually run.
 */
export const ThreadDevServerInfo = Schema.Struct({
  threadId: ThreadId,
  status: DevServerStatus,
  detections: Schema.Array(DevServerDetection),
  commandOverride: Schema.NullOr(TrimmedNonEmptyString),
  /** Command the server would spawn right now, override applied. */
  resolvedCommand: Schema.NullOr(TrimmedNonEmptyString),
  /** Hostname this thread would be published at, whether or not it is running. */
  hostname: Schema.NullOr(TrimmedNonEmptyString),
  /**
   * Loopback port reserved for this thread by the last allocation, whether or
   * not a process is up. `status.internalPort` only exists while the dev server
   * runs; this one survives a stop, which is what lets a caller write the right
   * port into the project's config before starting anything.
   */
  allocatedPort: Schema.NullOr(NonNegativeInt),
  /**
   * When public access expires, whether or not a process is up. Read from the
   * projection rather than from runtime state: the grant belongs to the
   * workspace and outlives any single boot of the dev server.
   */
  publicUntil: Schema.NullOr(IsoDateTime),
});
export type ThreadDevServerInfo = typeof ThreadDevServerInfo.Type;

export const StopThreadDevServerInput = Schema.Struct({
  threadId: ThreadId,
});
export type StopThreadDevServerInput = typeof StopThreadDevServerInput.Type;

export const UpdateThreadDevCommandInput = Schema.Struct({
  threadId: ThreadId,
  devCommand: Schema.NullOr(TrimmedNonEmptyString),
});
export type UpdateThreadDevCommandInput = typeof UpdateThreadDevCommandInput.Type;

/**
 * Publishes or unpublishes a thread's preview. The caller says whether it
 * should be public, never for how long — the window is the server's to set.
 */
export const SetThreadDevServerVisibilityInput = Schema.Struct({
  threadId: ThreadId,
  isPublic: Schema.Boolean,
});
export type SetThreadDevServerVisibilityInput = typeof SetThreadDevServerVisibilityInput.Type;

/**
 * One person's inbox state for one thread.
 *
 * Sent as a set on connect and then kept current by the `thread.settled` and
 * `thread.snoozed` domain events, which the server delivers only to the
 * connection they belong to.
 */
export const OrchestrationThreadUserState = Schema.Struct({
  threadId: ThreadId,
  settledAt: Schema.NullOr(IsoDateTime),
  snoozedUntil: Schema.NullOr(IsoDateTime),
  lastVisitedAt: Schema.NullOr(IsoDateTime),
  /** When a snooze last ended on its own. Null when it never has. */
  wokenAt: Schema.NullOr(IsoDateTime),
});
export type OrchestrationThreadUserState = typeof OrchestrationThreadUserState.Type;

export const GetThreadUserStateInput = Schema.Struct({});
export type GetThreadUserStateInput = typeof GetThreadUserStateInput.Type;

/**
 * Every row the caller owns.
 *
 * Only threads they have acted on have one, so this is proportional to what a
 * person has touched rather than to how many threads exist — which is why it
 * can travel on its own instead of widening the shell snapshot, whose whole
 * value is being computed once and shared by every connection.
 */
export const ThreadUserStateSnapshot = Schema.Struct({
  states: Schema.Array(OrchestrationThreadUserState),
});
export type ThreadUserStateSnapshot = typeof ThreadUserStateSnapshot.Type;

/**
 * Record that the caller looked at a thread.
 *
 * Not a command: this is stamped on every thread open, and putting one row in
 * the event log per click would grow the log that already decides how fast a
 * thread opens. Migration 052 carries the full argument.
 *
 * `visitedAt` moves forward only, unless `allowRewind` is set — which is how
 * mark-as-unread works, and the only way the value ever goes back.
 */
export const MarkThreadVisitedInput = Schema.Struct({
  threadId: ThreadId,
  visitedAt: IsoDateTime,
  allowRewind: Schema.optional(Schema.Boolean),
});
export type MarkThreadVisitedInput = typeof MarkThreadVisitedInput.Type;

export const ThreadDevServerStateChangedPayload = Schema.Struct({
  threadId: ThreadId,
  status: DevServerStatus,
});
export type ThreadDevServerStateChangedPayload = typeof ThreadDevServerStateChangedPayload.Type;

export const OrchestrationRpcSchemas = {
  getSnapshot: {
    input: OrchestrationGetSnapshotInput,
    output: OrchestrationGetSnapshotResult,
  },
  repairState: {
    input: OrchestrationRepairStateInput,
    output: OrchestrationRepairStateResult,
  },
  dispatchCommand: {
    input: ClientOrchestrationCommand,
    output: DispatchResult,
  },
  getTurnDiff: {
    input: OrchestrationGetTurnDiffInput,
    output: OrchestrationGetTurnDiffResult,
  },
  getFullThreadDiff: {
    input: OrchestrationGetFullThreadDiffInput,
    output: OrchestrationGetFullThreadDiffResult,
  },
  replayEvents: {
    input: OrchestrationReplayEventsInput,
    output: OrchestrationReplayEventsResult,
  },
  subscribeShell: {
    input: OrchestrationSubscribeShellInput,
    output: Schema.Void,
  },
  unsubscribeShell: {
    input: OrchestrationUnsubscribeShellInput,
    output: Schema.Void,
  },
  subscribeThread: {
    input: OrchestrationSubscribeThreadInput,
    output: Schema.Void,
  },
  unsubscribeThread: {
    input: OrchestrationUnsubscribeThreadInput,
    output: Schema.Void,
  },
  loadOlderThreadHistory: {
    input: OrchestrationLoadOlderThreadHistoryInput,
    output: OrchestrationLoadOlderThreadHistoryResult,
  },
  replayThreadEvents: {
    input: OrchestrationReplayThreadEventsInput,
    output: OrchestrationReplayThreadEventsResult,
  },
  setProjectRepoCredentials: {
    input: SetProjectRepoCredentialsInput,
    output: SetProjectRepoCredentialsResult,
  },
  validateProjectRepoCredentials: {
    input: ValidateProjectRepoCredentialsInput,
    output: ValidateProjectRepoCredentialsResult,
  },
  clearProjectRepoCredentials: {
    input: ClearProjectRepoCredentialsInput,
    output: Schema.Void,
  },
  createProjectFromRepository: {
    input: CreateProjectFromRepositoryInput,
    output: CreateProjectFromRepositoryResult,
  },
  startThreadDevServer: {
    input: StartThreadDevServerInput,
    output: DevServerStatus,
  },
  stopThreadDevServer: {
    input: StopThreadDevServerInput,
    output: DevServerStatus,
  },
  getThreadDevServer: {
    input: GetThreadDevServerInput,
    output: ThreadDevServerInfo,
  },
  updateThreadDevCommand: {
    input: UpdateThreadDevCommandInput,
    output: Schema.Void,
  },
  setThreadDevServerVisibility: {
    input: SetThreadDevServerVisibilityInput,
    output: ThreadDevServerInfo,
  },
  getThreadUserState: {
    input: GetThreadUserStateInput,
    output: ThreadUserStateSnapshot,
  },
  markThreadVisited: {
    input: MarkThreadVisitedInput,
    output: Schema.Void,
  },
} as const;
